import * as React from 'react'
import { ChevronRight, TriangleAlert } from 'lucide-react'
import type {
  AccountStatDto,
  ActiveHitDto,
  ClaimReceiptDto,
  HuntLiveDto,
  HuntPoll,
  SearchCallDto,
  WorkerCallDto,
} from '@/hooks/use-automation'
import { cn } from '@/lib/cn'
import {
  count,
  duration,
  failed,
  kb,
  shortAccount,
  shortEgress,
  shortId,
  stamp,
  throttleThreshold,
  type Digest,
  type Tone,
  type Verdict,
} from './hunt-digest'
import { fleetRole, fleetTag, roleName, roleTag, type WorkerRole } from './hunt-fleet'

/**
 * The four panels of the hunt view.
 *
 * Each is a `React.memo` component receiving already-derived values, so a poll
 * that changes nothing a tab shows costs that tab one `Object.is`. Nothing here
 * derives; everything comes from `buildDigest`.
 *
 * ── Why grid and not table ──────────────────────────────────────────
 * Auto table layout re-measures every cell on every repaint, and these lists
 * repaint 1.4 times a second for hours. CSS grid with a fixed module-scope
 * template costs nothing. The price is writing `tabular` explicitly, since the
 * stylesheet only auto-applies it inside `<table>`.
 */

/* ------------------------------------------------------------------ *
 * Shared vocabulary
 * ------------------------------------------------------------------ */

/**
 * A "copied ✓" flag that cleans up after itself.
 *
 * Three copy buttons each spelled this out as `setCopied(true)` plus a bare
 * `setTimeout`, and all three carried the same two faults. The timer was never
 * cleared on unmount — this panel lives in a drawer that closes, and 1.5s is
 * comfortably long enough to close it in — and a second click inside the window
 * started a second timer without cancelling the first, so the earlier one's
 * expiry cleared the flag while the newer copy was still fresh.
 *
 * Lives here rather than in `hunt-digest`, which is deliberately React-free: a
 * hook in a module documented as pure derivation is the kind of small erosion
 * that makes the next person stop trusting the boundary.
 */
/* ------------------------------------------------------------------ *
 * Which fleet a worker belongs to
 * ------------------------------------------------------------------ */

/**
 * The boundaries of the shared worker index space, for the whole panel.
 *
 * The fleets number into ONE space — the schedule watch takes `0 .. watch-1`,
 * the shift list the band above it, and the GA fleet everything above that — so
 * a bare worker number cannot say which fleet it came from, and most rows here
 * carry nothing else. A poll record is `{worker: 6}` and no more.
 *
 * ── Why this is two integers and not one ────────────────────────────
 * It was one — the watch count — because with two fleets a single comparison
 * decided everything above it. A third fleet cannot be placed by one boundary,
 * and getting it wrong is silent: every GA worker would render as `s7` and read
 * as a shift-list worker on a tab that does not list it.
 *
 * A context rather than props because these are two integers every tab needs and
 * nothing changes: threading them would touch eight components to deliver the
 * same values unchanged to each. Both default to `undefined`, which reads as "all
 * watch" — the correct rendering for a snapshot retained before the other fleets
 * existed.
 *
 * The undefineds do NOT both mean the same thing. A missing `watch` is genuinely
 * unknown. A missing `gql` is a ZERO: the payload ships that block as `null`
 * exactly when its count is 0, so `view.gql?.workers` is undefined precisely
 * when no shift-list fleet was armed — a fact, not a gap.
 *
 * Two boundaries because there are three fleets. The GA fleet sits at the top of
 * the space and needs no boundary of its own: everything above the second band
 * is its. Retained snapshots from the old availability wait armed GA alone, so
 * both numbers below it can be zero. The retired job fleet has no band; a
 * stored row that still carries its role is named from the role, never from
 * the index.
 */
const WorkerFleetsContext = React.createContext<{
  watch?: number
  gql?: number
}>({})

export function WatchWorkersProvider(p: {
  watchWorkers: number | undefined
  gqlWorkers?: number | undefined
  children: React.ReactNode
}) {
  /*
   * Memoised on the numbers, not rebuilt per render.
   *
   * A fresh object literal here would be a new context value on every parent
   * render, re-rendering every consumer — and the parent re-renders on a 700ms
   * poll, with the worker table among the consumers.
   */
  const value = React.useMemo(
    () => ({ watch: p.watchWorkers, gql: p.gqlWorkers }),
    [p.watchWorkers, p.gqlWorkers]
  )
  return <WorkerFleetsContext.Provider value={value}>{p.children}</WorkerFleetsContext.Provider>
}

/**
 * The panel's name for a record that carries only a worker number.
 *
 * The arithmetic itself lives in `hunt-fleet.ts`, where it is reachable by a
 * check. This is only the part that needs a renderer: reading the two boundaries
 * off the context, and holding the callback stable across a 700ms poll.
 *
 * Prefer `roleTag` on any row that carries a role — see its note for why.
 */
export function useWorkerTag(): (worker: number) => string {
  const { watch, gql } = React.useContext(WorkerFleetsContext)
  return React.useCallback((worker: number) => fleetTag(worker, watch, gql), [watch, gql])
}

/**
 * The fleet boundaries themselves, for a caller that GROUPS rather than names —
 * see `fleetRole`.
 *
 * The context value is memoised on the numbers, so this is stable across a poll
 * and safe in a dependency array.
 */
export function useWorkerFleets(): { watch?: number; gql?: number } {
  return React.useContext(WorkerFleetsContext)
}

export { roleName, roleTag }

export function useCopiedFlag(ms = 1500): [boolean, () => void] {
  const [copied, setCopied] = React.useState(false)
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  React.useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current)
    },
    []
  )

  const mark = React.useCallback(() => {
    setCopied(true)
    if (timer.current !== null) clearTimeout(timer.current)
    timer.current = setTimeout(() => setCopied(false), ms)
  }, [ms])

  return [copied, mark]
}

/*
 * Each grid carries its own floor — the sum of its fixed tracks, minmax floors
 * and gaps. These lists live inside drawers that can be narrower than the grid
 * (a 768-1024 tablet gets the full-screen drawer), and without a floor the 1fr
 * column silently collapses and the fixed columns clip. With one, the nearest
 * `overflow-x-auto` container gives the list a clean sideways scroll instead —
 * columnar alignment is the entire value of these views, so they scroll, not
 * reflow.
 */
const WORKER_COLS =
  'grid min-w-[45.25rem] grid-cols-[3px_2.25rem_3.5rem_2.75rem_minmax(4.5rem,1fr)_3.75rem_2.4rem_2.4rem_2.4rem_2.4rem_2.4rem_3.5rem_3.5rem_2.75rem] gap-x-2'

const REQ_COLS =
  'grid min-w-[41.5rem] grid-cols-[5.5rem_2rem_3.25rem_minmax(4.5rem,1fr)_2.5rem_3.75rem_5rem_4.5rem_5rem_1rem] gap-x-2'

const EDGE: Readonly<Record<Tone, string>> = Object.freeze({
  ok: 'bg-line',
  warn: 'bg-warn',
  danger: 'bg-danger',
})

const METER: Readonly<Record<Tone, string>> = Object.freeze({
  ok: 'bg-ok',
  warn: 'bg-warn',
  danger: 'bg-danger',
})

const TILE_EDGE: Readonly<Record<Tone | 'neutral', string>> = Object.freeze({
  neutral: 'border-l-line',
  ok: 'border-l-ok',
  warn: 'border-l-warn',
  danger: 'border-l-danger',
})

const TILE_VALUE: Readonly<Record<Tone | 'neutral', string>> = Object.freeze({
  neutral: 'text-fg',
  ok: 'text-ok',
  warn: 'text-warn',
  danger: 'text-danger',
})

/**
 * What a round trip has to be before it is worth LOOKING at.
 *
 * ── Where the numbers came from ─────────────────────────────────────
 * They were 150 / 250, chosen before anything had been measured, and against the
 * system that actually exists they classified reality as an emergency: a check
 * goes out through an ISP proxy to a CloudFront PoP, the best any address has
 * ever produced is ~163ms, and a normal one is 240–340ms. So every value on
 * every screen was amber or red — five workers, ten columns, permanently.
 *
 * The bands are now drawn where an operator would actually DO something:
 *
 *   ≤ 350ms   in band. What the proxy costs, and fast enough to win a race.
 *   ≤ 600ms   slower than this system's normal. Look at the address.
 *   > 600ms   something is wrong.
 *
 * ── Why in-band is green ────────────────────────────────────────────
 * It was ordinary grey ink, on the argument that colour must be rare to be a
 * signal and that 300ms through a proxy is neither good nor bad — it is the
 * price. That argument is right for a dashboard where latency is overhead. It is
 * wrong here, because on this screen latency IS the product: the whole run is a
 * race decided on round trip, and a worker sitting at 240ms is not "nothing to
 * report", it is the thing that wins. Green states that, and green next to green
 * next to green makes the one address that has drifted 80ms adrift visible as a
 * gap in the wall rather than as one grey number among five.
 *
 * The rarity argument survives intact where it actually pays: amber and red
 * still mean an exception and still appear only for one. Measured, not assumed —
 * amber is the brightest of the three in dark mode (1.17× the green's luminance)
 * and the darkest in light, so an exception out-shouts the baseline in both.
 */
const RTT_NORMAL_MS = 350
const RTT_SLOW_MS = 600

function rttTone(ms: number): string {
  if (ms <= RTT_NORMAL_MS) return 'text-ok'
  return ms <= RTT_SLOW_MS ? 'text-warn' : 'text-danger'
}

/** The same bands, as a `Tile` tone. One source, so a tile and a row agree. */
function rttToneKey(ms: number): Tone | 'neutral' {
  // Zero is "no measurement yet", which is not a fast one.
  if (ms === 0) return 'neutral'
  if (ms <= RTT_NORMAL_MS) return 'ok'
  return ms <= RTT_SLOW_MS ? 'warn' : 'danger'
}

/* ------------------------------------------------------------------ *
 * Tiles
 * ------------------------------------------------------------------ */

const Tile = React.memo(function Tile({
  label,
  value,
  sub,
  title,
  tone = 'neutral',
}: {
  label: string
  value: string
  /**
   * A second, quieter line — for the SPAN a number covers.
   *
   * Added because a window count and a run total sitting side by side with no
   * labels read as a miscount: `refused 1` beside `429 —` looks like a tally
   * that has lost one, when in fact the refusal simply happened more than five
   * hundred calls ago. Naming the span on each tile is the whole fix.
   */
  sub?: string
  title?: string
  tone?: Tone | 'neutral'
}) {
  return (
    <div
      title={title}
      /*
       * `min-w-0` plus truncation on every child.
       *
       * The grid track is `1fr` of an auto-fill column, so a tile with a long
       * value would otherwise set its own floor and push the track wider than
       * its siblings — which is how a row of ten tiles ends up as a row of eight
       * with two hanging off the edge.
       */
      className={cn(
        'flex min-w-0 flex-col gap-[3px] rounded-md border border-l-2 border-line bg-s1 px-2 py-1',
        TILE_EDGE[tone]
      )}
    >
      <span className="truncate text-[9.5px] font-medium tracking-[0.06em] text-faint uppercase">
        {label}
      </span>
      <span className={cn('tabular truncate text-[14px] leading-none', TILE_VALUE[tone])}>
        {value}
      </span>
      {sub === undefined ? null : (
        <span className="tabular truncate text-[9.5px] leading-none text-faint">{sub}</span>
      )}
    </div>
  )
})

/**
 * The tile grid: as many per row as will fit, never a fixed count.
 *
 * A fixed `sm:grid-cols-4` decided the tile width from the column count rather
 * than from the content, so ten tiles became three rows with two empty cells at
 * the end — and each tile was twice as wide as the four characters it held. Two
 * thirds of the strip was padding.
 *
 * `auto-fill` inverts that: the tile declares the narrowest it can usefully be
 * and the browser fits as many as the panel allows, so the same strip is two
 * rows on a half-screen drawer and one on a wide one, with no breakpoints to
 * keep in step with the number of tiles. Adding an eleventh tile now costs
 * nothing; under the old grid it silently created a third row.
 */
const TILE_GRID = 'grid grid-cols-[repeat(auto-fill,minmax(6.25rem,1fr))] gap-1'

/**
 * Mirrors `pageSize` in `search-cards.ts`.
 *
 * Used only to decide when the card high-water mark deserves a warning colour:
 * an answer that filled the page means the watched schedule could be sitting on
 * a page nothing asks for, because `nextToken` is deliberately ignored. If the
 * two ever drift, one tile changes colour a little early or a little late and
 * nothing else depends on it — which is why a mirrored constant is preferable to
 * shipping the page size on every poll.
 */
const GQL_PAGE_SIZE = 1000

/**
 * One HTTP status, over the recent window, with the run total beneath it.
 *
 * The two spans are the point. A refusal older than the window is genuinely
 * gone from `now` — which is correct, and is exactly what made the panel look
 * wrong: `refused 1` beside `429 —` reads as a tally that has lost one rather
 * than as two measurements of different things.
 *
 * So the colour is driven by `run`, not by `now`. A 429 that has aged out of the
 * window is still a 429 that happened, and a tile that goes quiet the moment it
 * scrolls out of range would hide the very thing an operator opened the panel to
 * find.
 */
const StatusTile = React.memo(function StatusTile({
  label,
  now,
  run,
  span,
  tone,
}: {
  label: string
  now: number
  run: number
  span: string
  tone: Tone
}) {
  return (
    <Tile
      label={label}
      value={now > 0 ? count(now) : '—'}
      /*
        A clean run says "none", not "last 489".

        Three of these sit side by side and on a healthy run all three read `—`
        with a window caption under them — nine lines of text stating that
        nothing happened, in the same visual weight as the numbers that did. The
        span only needs naming when there is a count for it to qualify.
      */
      sub={run > 0 ? `${span} · ${count(run)} this run` : now > 0 ? span : 'none'}
      title={
        run === 0
          ? `No ${label} responses at all this run.`
          : `${count(now)} in the ${span} checks, ${count(run)} since the hunt started. A refusal older than the window drops out of the first number and stays in the second.`
      }
      tone={run > 0 ? tone : 'neutral'}
    />
  )
})

/* ------------------------------------------------------------------ *
 * The worker strip (Overview)
 * ------------------------------------------------------------------ */

type WorkerRow = HuntLiveDto['workers'][number]

const WorkerLine = React.memo(function WorkerLine({
  w,
  tone,
  prefix,
  focused,
  arming,
  onFocus,
}: {
  w: WorkerRow
  tone: Tone
  prefix: string
  focused: boolean
  arming: boolean
  onFocus: (worker: number) => void
}) {
  const threshold = throttleThreshold(w.bestTtfbMs)
  const slowNow = threshold > 0 && w.lastMs > 0 && w.lastMs >= threshold

  return (
    <button
      type="button"
      onClick={() => onFocus(w.worker)}
      title={
        w.parked
          ? `no address — the ISP pool had none free · ${w.account}`
          : `${w.egress} · ${w.account}`
      }
      className={cn(
        WORKER_COLS,
        'tabular w-full items-center rounded-sm py-0.5 text-left text-[11.5px]',
        focused ? 'bg-accent-soft text-fg' : 'hover:bg-s2'
      )}
    >
      <span className={cn('h-4 w-[3px] rounded-full', EDGE[tone])} aria-hidden="true" />
      <span className="text-fg">{roleTag(w)}</span>
      {/*
        Coloured, because "parked" is a fault and every other value here is not.
        A worker with no address is not watching at all, and this cell is the
        only place in the row that can say so — the counts beside it are frozen
        at whatever its last real check produced.
      */}
      <span className={cn('truncate', w.parked ? 'text-danger' : 'text-faint')}>
        {w.parked ? 'parked' : shortEgress(w.egress, prefix)}
      </span>
      <span
        className={cn(
          w.accountsTotal > 0 && w.accounts === 0 ? 'text-danger' : 'text-faint'
        )}
      >
        {w.accountsTotal > 0 ? `${w.accounts}/${w.accountsTotal}` : '—'}
      </span>
      <span className="truncate text-accent/70">{shortAccount(w.account)}</span>
      <span className="text-muted">{w.polls > 0 ? count(w.polls) : '0'}</span>
      <span className={w.errors > 0 ? 'text-warn' : 'text-faint'}>
        {w.errors > 0 ? w.errors : '–'}
      </span>
      {/*
        The three refusals, over the same 500-call window as the tiles above —
        so a worker's numbers always add up to the totals it sits under. A whole
        run's counts would only climb; these fall again when the cause is fixed.
      */}
      <span className={w.s403 > 0 ? 'text-danger' : 'text-faint'}>{w.s403 > 0 ? w.s403 : '–'}</span>
      <span className={w.s405 > 0 ? 'text-warn' : 'text-faint'}>{w.s405 > 0 ? w.s405 : '–'}</span>
      <span className={w.s429 > 0 ? 'text-warn' : 'text-faint'}>{w.s429 > 0 ? w.s429 : '–'}</span>
      {/*
        A worker getting NO reply reads `dead`, not a faint dash.

        Status 0 is the console's marker for a request that never came back, and
        rendering it as `–` made the worst state a worker can be in look like the
        quietest — which is how a worker sat at 1,019 consecutive failures beside
        four healthy ones without anybody noticing. `polls` separates it from a
        worker that simply has not reported yet, which is also 0.
      */}
      <span
        className={
          w.polls === 0
            ? 'text-faint'
            : w.lastStatus === 0 || failed(w.lastStatus)
              ? 'text-danger'
              : 'text-ok'
        }
      >
        {w.polls === 0 ? '–' : w.lastStatus === 0 ? 'dead' : w.lastStatus}
      </span>
      <span className={w.lastMs > 0 ? rttTone(w.lastMs) : 'text-faint'}>
        {w.lastMs > 0 ? `${w.lastMs}ms` : '–'}
      </span>
      {/*
        The AVERAGE over the window, not the all-time best.

        `best` only ever fell and said nothing about how an address is behaving
        now — five all-time bests side by side is trivia. Five averages, one of
        them 80ms adrift, is a finding. The ▲ still marks a last check past the
        address's own throttle threshold, which is the signal `best` existed to
        support and which survives without showing the number itself.
      */}
      <span className={cn(w.avgMs > 0 ? rttTone(w.avgMs) : 'text-faint', slowNow && 'text-warn')}>
        {w.avgMs > 0 ? `${w.avgMs}ms` : '–'}
        {slowNow ? '▲' : ''}
      </span>
      {/*
        The column that finds a stalled worker.

        One frozen on a hung socket keeps every other number and looks perfectly
        healthy; only the time since it last COMPLETED gives it away.
      */}
      <span className={cn(w.lastAgoMs !== null && w.lastAgoMs > 5_000 && 'text-warn')}>
        {w.lastAgoMs === null ? (arming ? 'arming' : '–') : `${(w.lastAgoMs / 1000).toFixed(1)}s`}
      </span>
    </button>
  )
})

const WORKER_HEAD = [
  '',
  // `w0` / `s6` / `j11` / `ga14` — the letter is the fleet, the number is the
  // shared slot. Four letters now, so the header names the space rather than
  // listing it.
  'worker',
  'egress',
  'ring',
  'account',
  'checks',
  'err',
  '403',
  '405',
  '429',
  'last',
  'rtt',
  'avg',
  'ago',
]

/* ------------------------------------------------------------------ *
 * Latency meter (Workers)
 * ------------------------------------------------------------------ */

/**
 * One address's latency, drawn against its own floor.
 *
 * The throttle detector, made visible. A healthy address has its `best` tick
 * immediately left of its fill; a throttled one has the tick stranded far left
 * with the fill several times further out. That SHAPE is the diagnosis, and it
 * is readable without parsing three numbers.
 *
 * Every meter shares one scale (`max`), because the comparison being made is
 * between workers. Per-worker scaling would draw a throttled address and a
 * healthy one identically — each full against its own maximum.
 *
 * Only `width` and `left` change, inside a fixed-height parent, so no repaint
 * can reflow the rows below it.
 */
const LatencyMeter = React.memo(function LatencyMeter({
  best,
  p50,
  p90,
  max,
  tone,
}: {
  best: number
  p50: number
  p90: number
  max: number
  tone: Tone
}) {
  const pct = (v: number) => `${Math.max(0, Math.min(100, (v / max) * 100)).toFixed(1)}%`
  return (
    <div className="relative h-1.5 min-w-0 flex-1 rounded-full bg-s3">
      <span
        className="absolute inset-y-0 left-0 rounded-full bg-ok/30"
        style={{ width: pct(p90) }}
      />
      <span
        className={cn('absolute inset-y-0 left-0 rounded-full', METER[tone])}
        style={{ width: pct(p50) }}
      />
      {best > 0 ? (
        <span className="absolute inset-y-[-2px] w-px bg-fg/70" style={{ left: pct(best) }} />
      ) : null}
    </div>
  )
})

/* ------------------------------------------------------------------ *
 * Workers tab
 * ------------------------------------------------------------------ */

const WorkerBlock = React.memo(function WorkerBlock({
  w,
  win,
  tone,
  meterMax,
  verdicts,
  live,
  focused,
  onFocus,
}: {
  w: WorkerRow
  win: Digest['per'][number]
  tone: Tone
  meterMax: number
  verdicts: Verdict[]
  live: boolean
  focused: boolean
  onFocus: (worker: number) => void
}) {
  const threshold = throttleThreshold(w.bestTtfbMs)
  const p50 = win?.p50Ttfb ?? 0
  const ratio = w.bestTtfbMs > 0 && p50 > 0 ? p50 / w.bestTtfbMs : 0

  const state =
    w.lastAgoMs !== null && w.lastAgoMs > 5_000
      ? 'STALLED'
      : threshold > 0 && p50 >= threshold
        ? 'THROTTLED'
        : w.polls === 0
          ? 'arming'
          : 'steady'

  const ring =
    w.accountsTotal === 0
      ? { text: 'no ring', cls: 'text-faint' }
      : w.accounts === 0
        ? { text: 'NO RING', cls: 'text-danger' }
        : win !== undefined && w.accountsTotal > 1 && win.n >= 4 && win.accounts.length === 1
          ? { text: 'STALLED', cls: 'text-warn' }
          : { text: 'rotating', cls: 'text-ok' }

  return (
    <div
      className="px-3 py-1.5"
      // Skip layout and paint for blocks scrolled out of view. The browser keeps
      // the scroll height; it just does no work for rows nobody can see.
      style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 58px' }}
    >
      <button
        type="button"
        onClick={() => onFocus(w.worker)}
        className={cn(
          'tabular flex w-full items-center gap-2 rounded-sm text-left text-[11.5px]',
          focused ? 'bg-accent-soft' : 'hover:bg-s2'
        )}
      >
        <span className={cn('h-4 w-[3px] shrink-0 rounded-full', EDGE[tone])} aria-hidden="true" />
        <span className="text-fg">{roleTag(w)}</span>
        <span className="truncate text-faint">{w.egress}</span>
        <span className="ml-auto shrink-0 text-muted">
          {count(w.polls)} checks · {w.errors} err ·{' '}
          {w.lastStatus === 0 ? '–' : `last ${w.lastStatus}`} · {w.lastMs}ms ·{' '}
          {w.lastAgoMs === null ? '–' : `${(w.lastAgoMs / 1000).toFixed(1)}s ago`}
        </span>
      </button>

      <div className="tabular mt-1 flex items-center gap-2 pl-[11px] text-[11px]">
        <span
          className={cn(
            'w-[5rem] shrink-0',
            state === 'STALLED'
              ? 'text-danger'
              : state === 'THROTTLED'
                ? 'text-warn'
                : 'text-faint'
          )}
        >
          {state}
        </span>
        <LatencyMeter best={w.bestTtfbMs} p50={p50} p90={win?.p90Ttfb ?? 0} max={meterMax} tone={tone} />
        <span className="shrink-0 text-faint">
          best {w.bestTtfbMs > 0 ? w.bestTtfbMs : '–'} · p50 {p50 > 0 ? p50 : '–'} · p90{' '}
          {win?.p90Ttfb ?? '–'}
          {ratio >= 2 ? <span className="text-warn"> {ratio.toFixed(1)}×</span> : null}
        </span>
      </div>

      <div className="tabular mt-0.5 flex items-center gap-2 pl-[11px] text-[11px]">
        <span className="shrink-0 text-faint">
          ring {w.accountsTotal > 0 ? `${w.accounts}/${w.accountsTotal}` : '0/0'}
        </span>
        <span className={cn('shrink-0', ring.cls)}>{ring.text}</span>
        <span className="truncate text-muted">
          {win === undefined || win.accounts.length === 0
            ? '—'
            : win.accounts.map(shortAccount).join(' · ')}
        </span>
      </div>

      {verdicts.map((v, i) => (
        <p
          key={i}
          className={cn(
            'mt-0.5 pl-[11px] text-[11px]',
            v.tone === 'danger' ? 'text-danger' : 'text-warn'
          )}
        >
          ⚠ {live ? v.text : v.pastText}
        </p>
      ))}
    </div>
  )
})

export const WorkersTab = React.memo(function WorkersTab({
  hunt,
  digest,
  focus,
  onFocus,
}: {
  hunt: HuntLiveDto
  digest: Digest
  focus: number | null
  onFocus: (worker: number) => void
}) {
  if (hunt.workers.length === 0) {
    return (
      <p className="px-3 py-2 text-[11px] text-faint">
        no workers launched yet — the hunt is still arming
      </p>
    )
  }
  return (
    <div className="divide-y divide-line/25">
      {hunt.workers.map((w) =>
        w == null ? null : (
          <WorkerBlock
            key={w.worker}
            w={w}
            win={digest.per[w.worker]}
            tone={digest.health[w.worker] ?? 'ok'}
            meterMax={digest.meterMax}
            verdicts={digest.verdicts.filter((v) => v.worker === w.worker)}
            live={hunt.live}
            focused={focus === w.worker}
            onFocus={onFocus}
          />
        )
      )}
      <p className="px-3 py-1.5 text-[10.5px] text-faint">
        meter: tick = this address&rsquo;s best ttfb, solid = p50, pale = p90. All workers share
        one scale, so a bar that is four times another&rsquo;s is four times slower.
      </p>
    </div>
  )
})

/* ------------------------------------------------------------------ *
 * Request rows (Requests + Errors)
 * ------------------------------------------------------------------ */

const RequestRow = React.memo(function RequestRow({
  p,
  prefix,
  expanded,
  onToggle,
  onFocusWorker,
}: {
  p: HuntPoll
  prefix: string
  expanded: boolean
  onToggle: (key: string) => void
  onFocusWorker: (worker: number) => void
}) {
  const key = `${p.at}-${p.worker}`
  const tag = useWorkerTag()
  const bad = failed(p.status)
  return (
    <div
      className="border-b border-line/25 px-3 py-1 last:border-0"
      style={{
        contentVisibility: 'auto',
        containIntrinsicSize: expanded ? 'auto 88px' : 'auto 24px',
      }}
    >
      <div className={cn(REQ_COLS, 'tabular items-center text-[11px]')}>
        <span className="text-faint">{stamp(p.at)}</span>
        <button
          type="button"
          onClick={() => onFocusWorker(p.worker)}
          className="text-left text-fg hover:text-accent"
        >
          {tag(p.worker)}
        </button>
        <span className="truncate text-faint">{shortEgress(p.egress, prefix)}</span>
        <span className="truncate text-accent/70" title={p.account}>
          {shortAccount(p.account)}
        </span>
        <span className={p.status === 0 ? 'text-danger' : bad ? 'text-danger' : 'text-ok'}>
          {p.status === 0 ? 'ERR' : p.status}
        </span>
        <span className={bad ? 'text-muted' : rttTone(p.ms)}>{p.ms > 0 ? `${p.ms}ms` : '—'}</span>
        {/* The split that says WHY a check was slow: network/origin, or transfer. */}
        <span className="text-faint">
          {p.status === 0 ? 'transport' : `${p.ttfbMs} / ${Math.max(0, p.ms - p.ttfbMs)}`}
        </span>
        <span className={p.bytes === 0 ? 'text-faint' : p.enc === 'gzip' ? 'text-faint' : 'text-warn'}>
          {p.bytes === 0 ? '—' : `${kb(p.bytes)}${p.enc === 'gzip' ? ' gz' : ''}`}
        </span>
        <span
          className={cn(
            'truncate',
            p.active ? 'rounded-xs bg-ok-soft px-1 font-medium text-ok' : 'text-faint'
          )}
        >
          {p.active ? 'ACTIVE ✓' : (p.scheduleStatus ?? '—')}
          {p.warm ? '' : ' · cold'}
        </span>
        <button
          type="button"
          onClick={() => onToggle(key)}
          aria-label={expanded ? 'Hide response' : 'Show response'}
          aria-expanded={expanded}
          className="text-faint hover:text-muted"
        >
          <ChevronRight
            aria-hidden="true"
            className={cn('size-3.5 transition-transform', expanded && 'rotate-90')}
          />
        </button>
      </div>

      {/*
        The response, verbatim.

        `scheduleStatus` above is this system's READING of the body; this is the
        body. They agree until something changes shape, and that is the one
        moment worth telling apart — a hunt reporting INACTIVE forever looks
        identical whether the shift is closed or the field moved.
      */}
      {expanded && p.body !== '' ? (
        <pre className="mt-1 max-h-40 overflow-auto text-[10.5px] leading-relaxed break-words whitespace-pre-wrap text-muted/90">
          {p.body}
        </pre>
      ) : null}
      {expanded && p.body === '' ? (
        <p className="mt-1 text-[10.5px] text-faint">
          body no longer held — only the newest checks keep one
        </p>
      ) : null}
    </div>
  )
})

const REQ_HEAD = ['time', 'w/s', 'egress', 'account', 'st', 'rtt', 'ttfb/body', 'size', 'schedule', '']

/**
 * Everything the Requests tab needs to drive the full trace.
 *
 * Bundled into one object rather than eight props because they are one feature
 * and they change together — and because `RequestsTab` is `React.memo`'d, so a
 * prop that changes identity every render would defeat the whole hidden-tab
 * optimisation. The parent memoises this.
 */
export interface TraceControls {
  /** Whether the server is recording, and how many it holds. */
  on: boolean
  size: number
  /** The loaded snapshot, OLDEST first, or null when none has been fetched. */
  records: HuntPoll[] | null
  busy: boolean
  loading: boolean
  copied: boolean
  onToggle: () => void
  onLoad: () => void
  onClear: () => void
  onCopy: (rows: HuntPoll[]) => void
}

/**
 * One check as a line of text, for pasting somewhere that is not a browser.
 *
 * Fixed-width fields in a fixed order, so a thousand of them can be read with
 * `grep`, sorted, or diffed — which is what "give me the logs" actually means
 * once the list is longer than a screen.
 */
export function traceLine(p: HuntPoll): string {
  const t = new Date(p.at).toISOString().slice(11, 23)
  const status = p.status === 0 ? 'ERR' : String(p.status)
  const head =
    `${t}  w${p.worker}  ${p.egress.padEnd(22)}  ${p.account.padEnd(24)}  ` +
    `${status.padStart(3)}  ${String(p.ms).padStart(5)}ms  ttfb=${String(p.ttfbMs).padStart(4)}  ` +
    `${String(p.bytes).padStart(6)}B ${(p.enc || 'raw').padEnd(4)}  ${p.warm ? 'warm' : 'COLD'}  ` +
    `${p.active ? 'ACTIVE' : (p.scheduleStatus ?? '-')}`
  // The body only ever exists on failures, and it goes on its own indented line
  // so the columns above stay alignable.
  return p.body === '' ? head : `${head}\n    ${p.body}`
}

export const RequestsTab = React.memo(function RequestsTab({
  rows,
  digest,
  focus,
  all,
  onToggleAll,
  onClearFocus,
  onFocusWorker,
  url,
  trace,
}: {
  rows: HuntPoll[]
  /*
   * The whole digest, rather than `total` and `prefix` as separate props.
   *
   * Both derive from it, and passing them separately would mean this tab took
   * two values that change on EVERY poll — so its `React.memo` could never bail
   * while the tab was hidden, which is the entire reason the panels stay
   * mounted. One held reference is the difference.
   */
  digest: Digest
  focus: number | null
  all: boolean
  onToggleAll: () => void
  onClearFocus: () => void
  onFocusWorker: (worker: number) => void
  url: string
  trace: TraceControls
}) {
  const tag = useWorkerTag()
  const [open, setOpen] = React.useState<string | null>(null)
  const toggle = React.useCallback((key: string) => {
    setOpen((cur) => (cur === key ? null : key))
  }, [])
  const prefix = digest.egressPrefix

  /*
   * A loaded trace REPLACES the window, rather than sitting beside it.
   *
   * Two lists of the same checks, one thirty deep and one two thousand, is a
   * reliable way to read the wrong one. When a trace has been loaded it is the
   * more complete answer to the same question, so it takes over — and the
   * header says plainly which of the two is on screen.
   */
  const traced = trace.records
  const source = traced ?? rows
  const shown = React.useMemo(() => {
    if (traced === null) return rows
    const filtered = focus === null ? traced : traced.filter((p) => p.worker === focus)
    // Server order is oldest-first; every other list here is newest-first, and
    // switching that convention between two views of the same data is how a
    // pattern gets read backwards.
    return [...filtered].reverse()
  }, [traced, rows, focus])

  const total = traced === null ? digest.newestFirst.length : source.length

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-none flex-wrap items-center gap-x-2 gap-y-1 border-b border-line/60 px-3 py-1.5 text-[11.5px] text-faint">
        <span>
          {shown.length} of {total} checks · newest first
          {traced === null ? ' · live window' : ' · full trace'}
        </span>
        {traced === null && total > 20 ? (
          <button
            type="button"
            onClick={onToggleAll}
            className="rounded-xs px-1.5 text-accent hover:bg-s2"
          >
            {all ? 'newest 20' : `all ${total}`}
          </button>
        ) : null}

        {/* -------- the full trace -------- */}
        <button
          type="button"
          onClick={trace.onToggle}
          disabled={trace.busy}
          title={
            trace.on
              ? 'Stop recording every check'
              : 'Record every check for this customer, up to 2000'
          }
          className={cn(
            'rounded-xs px-1.5',
            trace.on ? 'bg-warn-soft text-warn' : 'text-accent hover:bg-s2',
            trace.busy && 'opacity-50'
          )}
        >
          {trace.on ? `recording · ${count(trace.size)}` : 'record all'}
        </button>
        {trace.on ? (
          <button
            type="button"
            onClick={trace.onLoad}
            disabled={trace.loading}
            className="rounded-xs px-1.5 text-accent hover:bg-s2"
          >
            {trace.loading ? 'loading…' : traced === null ? 'load trace' : 'refresh'}
          </button>
        ) : null}
        {traced === null ? null : (
          <>
            <button
              type="button"
              onClick={() => trace.onCopy(shown)}
              className="rounded-xs px-1.5 text-accent hover:bg-s2"
            >
              {trace.copied ? 'copied ✓' : 'copy as text'}
            </button>
            <button
              type="button"
              onClick={trace.onClear}
              className="rounded-xs px-1.5 text-faint hover:bg-s2 hover:text-muted"
            >
              back to live
            </button>
          </>
        )}

        <span className="ml-auto text-faint">click a worker to focus every tab</span>
      </div>

      {/* The header lives OUTSIDE the vertical scroller, so the two can only
          stay column-aligned if they scroll sideways together — hence this
          shared horizontal scroller around both. The body carries the grid's
          own floor so its rows never out-scroll the header. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-x-auto">
        <div
          className={cn(
            REQ_COLS,
            'flex-none border-b border-line/60 px-3 py-1 text-[10px] tracking-[0.06em] text-faint uppercase'
          )}
        >
          {REQ_HEAD.map((h, i) => (
            <span key={i} className="truncate">
              {h}
            </span>
          ))}
        </div>

        <div className="min-h-0 min-w-[41.5rem] flex-1 overflow-y-auto">
          {shown.length === 0 ? (
            focus === null ? (
              <p className="px-3 py-2 text-[11px] text-faint">no checks recorded yet</p>
            ) : (
              <p className="px-3 py-2 text-[11px] text-faint">
                {tag(focus)} has not completed a check in this {traced === null ? 'window' : 'trace'} ·{' '}
                <button
                  type="button"
                  onClick={onClearFocus}
                  className="rounded-xs px-1 text-accent hover:bg-s2"
                >
                  show all workers
                </button>
              </p>
            )
          ) : (
            shown.map((p) => (
              <RequestRow
                key={`${p.at}-${p.worker}-${p.status}`}
                p={p}
                prefix={prefix}
                expanded={open === `${p.at}-${p.worker}-${p.status}`}
                onToggle={toggle}
                onFocusWorker={onFocusWorker}
              />
            ))
          )}
        </div>
      </div>

      <div className="flex-none border-t border-line/40 px-3 py-1 text-[10.5px] break-all text-faint">
        GET {url} — the same on every row
      </div>
    </div>
  )
})

/* ------------------------------------------------------------------ *
 * Errors tab
 * ------------------------------------------------------------------ */

export const ErrorsTab = React.memo(function ErrorsTab({
  hunt,
  digest,
  focus,
  onFocusWorker,
  onClearFocus,
}: {
  hunt: HuntLiveDto
  digest: Digest
  focus: number | null
  onFocusWorker: (worker: number) => void
  onClearFocus: () => void
}) {
  const tag = useWorkerTag()
  const [open, setOpen] = React.useState<string | null>(null)
  const toggle = React.useCallback((key: string) => {
    setOpen((cur) => (cur === key ? null : key))
  }, [])

  /*
   * Every kept refusal for the WHOLE hunt, not the last two seconds.
   *
   * This used to read `digest.refusals`, which is filtered out of the shared
   * `recent` ring — thirty records, which at five gapless workers is under two
   * seconds of history. So a worker failing once in three could show 394 errors
   * in its counter and an empty list underneath it, because its four healthy
   * siblings had pushed every one of them out before the panel rendered. The
   * per-worker rings on the server exist for exactly this, and this is where
   * they are read.
   */
  const kept = React.useMemo(() => {
    const rings = hunt.workerErrors ?? []
    const rows: HuntPoll[] = []
    for (const [w, ring] of rings.entries()) {
      if (!ring) continue
      if (focus !== null && w !== focus) continue
      rows.push(...ring)
    }
    return rows.sort((a, b) => b.at - a.at)
  }, [hunt.workerErrors, focus])

  /*
   * The counts always arrive; the RECORDS only for the worker being read.
   *
   * Forty records each carrying a response body, times five workers, on a 700ms
   * poll is tens of kilobytes a second spent on a tab nobody has open — so the
   * server sends them for the selected worker and counts for everyone else. The
   * distinction has to be visible here, or an empty list under a count of 394
   * reads as data loss rather than as "you have not asked for it yet".
   */
  const counts = hunt.workerErrorCounts ?? []
  const totalKept = counts.reduce((n, c) => n + c, 0)
  const awaitingPick = focus === null && kept.length === 0 && totalKept > 0

  const statusMix = React.useMemo(() => {
    const by = new Map<string, number>()
    for (const p of kept) by.set(String(p.status), (by.get(String(p.status)) ?? 0) + 1)
    return [...by].sort((a, b) => b[1] - a[1])
  }, [kept])

  if (hunt.errors === 0 && kept.length === 0) {
    // The one empty state that should feel good.
    return (
      <div className="flex h-full items-center justify-center px-3 py-6 text-center text-[12px] text-ok">
        no refusals in {count(hunt.polls)} checks
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      {hunt.lastError ? (
        <div className="border-b border-line/60 bg-warn-soft/20 px-3 py-2">
          <div className="tabular flex flex-wrap items-center gap-x-2 text-[11.5px] text-warn">
            <TriangleAlert aria-hidden="true" className="size-3.5 shrink-0" />
            <span className="font-medium">LAST REFUSAL</span>
            <span>{stamp(hunt.lastError.at)}</span>
            <span>
              {tag(hunt.lastError.worker)} · {hunt.lastError.egress} · {hunt.lastError.status} ·{' '}
              {hunt.lastError.ms}ms
            </span>
          </div>
          <div className="tabular mt-0.5 text-[11px] text-warn/80">
            as {hunt.lastError.account}
          </div>
          <div className="tabular mt-0.5 text-[10.5px] break-all text-faint">
            GET {hunt.lastError.url}
          </div>
          <pre className="mt-1 max-h-32 overflow-auto text-[10.5px] leading-relaxed break-words whitespace-pre-wrap text-muted">
            {hunt.lastError.body}
          </pre>
        </div>
      ) : null}

      <div className="tabular border-b border-line/60 px-3 py-1.5 text-[11px] text-faint">
        <div>
          refusals per worker, whole hunt ·{' '}
          {hunt.workers
            .filter((w) => w != null)
            .map((w) => (
              <button
                key={w.worker}
                type="button"
                onClick={() => onFocusWorker(w.worker)}
                className={cn(
                  'px-1 hover:text-muted',
                  w.errors > 0 && 'text-warn',
                  focus === w.worker && 'rounded-xs bg-s3 text-fg'
                )}
                title={`${counts[w.worker] ?? 0} kept, with the response bodies`}
              >
                {roleTag(w)} {w.errors}
              </button>
            ))}
          {focus === null ? null : (
            <button
              type="button"
              onClick={onClearFocus}
              className="ml-1 rounded-xs px-1 text-accent hover:bg-s2"
            >
              clear
            </button>
          )}
        </div>
        <div className="mt-0.5">
          {kept.length === 0
            ? 'nothing kept yet'
            : `showing ${kept.length} kept refusal${kept.length === 1 ? '' : 's'}${
                focus === null ? ' across every worker' : ` for ${tag(focus)}`
              } · newest first · up to 40 per worker`}
          {statusMix.length > 0
            ? ` · ${statusMix.map(([s, n]) => `${s === '0' ? 'ERR' : s} × ${n}`).join(' · ')}`
            : ''}
        </div>
      </div>

      {kept.length === 0 ? (
        <p className="px-3 py-2 text-[11px] text-faint">
          {awaitingPick
            ? `${count(totalKept)} refusals are kept — click a worker above to load them with their response bodies`
            : hunt.errors === 0
              ? 'no refusals yet'
              : 'refusals were counted before this build started keeping them — the next one will be listed here'}
        </p>
      ) : (
        kept.map((p) => (
          <RequestRow
            key={`${p.at}-${p.worker}-${p.status}`}
            p={p}
            prefix={digest.egressPrefix}
            expanded={open === `${p.at}-${p.worker}-${p.status}`}
            onToggle={toggle}
            onFocusWorker={onFocusWorker}
          />
        ))
      )}
    </div>
  )
})

/* ------------------------------------------------------------------ *
 * Worker log — one worker, every call
 * ------------------------------------------------------------------ */

const LOG_COLS =
  'grid min-w-[40.5rem] grid-cols-[3.4rem_5.6rem_7.5rem_minmax(5rem,1fr)_2.6rem_3.8rem_3.4rem_5.5rem] gap-x-2'
const LOG_HEAD = ['#', 'time', 'account id', 'login', 'st', 'rtt', 'ttfb', 'schedule']

/**
 * One call. Deliberately a plain line, not a card.
 *
 * The whole value of a log is that two hundred of them can be read down a
 * column, so every field sits at a fixed width and nothing wraps. Colour carries
 * only the status, because that is the one field the eye should catch while
 * scrolling.
 */
const LogLine = React.memo(function LogLine({ c, prev }: { c: WorkerCallDto; prev?: WorkerCallDto }) {
  const bad = failed(c.status)
  /*
   * The GAP since this worker's previous call.
   *
   * The single most useful derived number here and it costs a subtraction. A
   * worker looping with no gap should show its own round trip and nothing more;
   * anything larger is time it was not watching — a backoff after a refusal, a
   * rotation, a stall — and none of that is visible from the timestamps alone
   * without doing this arithmetic in your head two hundred times.
   */
  const gap = prev === undefined ? null : c.at - prev.at
  const stalled = gap !== null && gap > c.ms + 150

  return (
    <div
      role="row"
      className={cn(
        LOG_COLS,
        'tabular items-center border-b border-line/20 px-3 py-[3px] text-[11px] last:border-0',
        // Refusals tinted at the ROW, not just in the status cell. Scanning two
        // hundred lines for a colour in one narrow column is reading; a tinted
        // row is seeing.
        bad && 'bg-danger-soft/20',
        c.active && 'bg-ok-soft/25'
      )}
      style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 20px' }}
    >
      <span role="cell" className="text-faint">
        #{c.seq}
      </span>
      <span role="cell" className="text-muted">
        {stamp(c.at)}
      </span>
      {/*
        Shortened from the MIDDLE, never the end.

        These ids share a long identical prefix and differ only in their last few
        characters, so ordinary truncation rendered five different accounts as
        five identical strings — in the column whose whole job is telling them
        apart. The full id is in the tooltip and in the text export, which is
        where a Mongo query gets it from.
      */}
      <span
        role="cell"
        className="truncate text-faint"
        title={c.accountId || 'the customer’s own session'}
      >
        {shortId(c.accountId)}
      </span>
      <span role="cell" className="truncate text-accent/80" title={c.account}>
        {shortAccount(c.account)}
      </span>
      <span
        role="cell"
        className={c.status === 0 ? 'text-danger' : bad ? 'text-danger' : 'text-ok'}
      >
        {c.status === 0 ? 'ERR' : c.status}
      </span>
      <span role="cell" className={bad ? 'text-muted' : rttTone(c.ms)}>
        {c.ms > 0 ? `${c.ms}ms` : '—'}
      </span>
      <span
        role="cell"
        className={cn('text-faint', stalled && 'text-warn')}
        title={
          stalled && gap !== null
            ? `${gap}ms since this worker's previous call — longer than the round trip, so it was idle in between`
            : undefined
        }
      >
        {stalled && gap !== null ? `+${gap}ms` : c.ttfbMs > 0 ? `${c.ttfbMs}` : '—'}
      </span>
        {/*
          What the server actually SAID, which the log had no column for.

          Every other field describes the request. Without this one, a worker
          watching correctly and a worker being handed a document this system no
          longer understands are the same row — both 200 at 240ms — and the
          difference between them is the difference between waiting and being
          broken. `—` is a call that failed; `?` is a 200 whose status could not
          be read, which is the case worth noticing.
        */}
      <span
        role="cell"
        className={cn(
          'truncate',
          c.active
            ? 'rounded-xs bg-ok-soft px-1 font-medium text-ok'
            : bad
              ? 'text-faint'
              : c.scheduleStatus == null
                ? 'text-warn'
                : 'text-faint'
        )}
        title={
          c.active
            ? 'This check saw the shift open'
            : bad
              ? 'The call failed — there was no schedule to read'
              : (c.scheduleStatus ??
                'The call succeeded but no status could be read from the body — either an empty response or a document shape this system does not recognise')
        }
      >
        {c.active ? 'ACTIVE ✓' : bad ? '—' : (c.scheduleStatus ?? '?')}
      </span>
    </div>
  )
})

/** Stable identity for an absent count array — see `WorkerLogTab`. */
const NO_COUNTS: readonly number[] = Object.freeze([])

/** One call as a text line, for pasting into anything that is not a browser. */
export function logLine(c: WorkerCallDto): string {
  const t = new Date(c.at).toISOString().slice(11, 23)
  const status = c.status === 0 ? 'ERR' : String(c.status)
  const schedule = c.active ? 'ACTIVE' : failed(c.status) ? '-' : (c.scheduleStatus ?? '?')
  return (
    `#${String(c.seq).padStart(6)}  ${t}  ${(c.accountId || '-').padEnd(26)}  ` +
    `${c.account.padEnd(22)}  ${status.padStart(3)}  ${String(c.ms).padStart(5)}ms  ` +
    `ttfb=${String(c.ttfbMs).padStart(4)}  ${schedule.padEnd(10)}  ${c.egress}`
  )
}

export const WorkerLogTab = React.memo(function WorkerLogTab({
  hunt,
  worker,
  onSelectWorker,
}: {
  hunt: HuntLiveDto
  worker: number | null
  onSelectWorker: (worker: number | null) => void
}) {
  const tag = useWorkerTag()
  const [copied, markCopied] = useCopiedFlag()
  /*
   * One shared empty array, not a fresh `[]` per render.
   *
   * The fallback is a prop on a memoised child, so allocating it inline makes
   * that memo miss on every poll for the whole time the field is absent — which
   * is exactly the case it is there to handle gracefully.
   */
  const counts = hunt.workerLogCounts ?? NO_COUNTS
  const cap = hunt.workerLogCap ?? 0

  /*
   * Only render a log the response actually says is this worker's.
   *
   * The records are fetched with `?worker=N`, so for one poll after a click the
   * cached response still holds the PREVIOUS worker's calls. `logWorker` is the
   * server saying whose these are, and checking it is what stops a list of w0's
   * calls appearing under a w3 heading — the one mistake this view cannot make,
   * because every conclusion drawn from it would be about the wrong worker.
   *
   * A retained post-mortem carries every worker and reports `logWorker: null`,
   * so it is trusted for whichever worker is selected.
   */
  const owned = hunt.logWorker === null || hunt.logWorker === worker
  const calls = worker === null || !owned ? null : (hunt.workerLog?.[worker] ?? [])

  // Newest first on screen — this is a live view, and the interesting end of a
  // live log is the end that is moving.
  const rows = React.useMemo(() => (calls === null ? [] : [...calls].reverse()), [calls])

  const onCopy = React.useCallback(() => {
    if (calls === null) return
    // Oldest first in the clipboard, whatever the screen shows: a log read
    // outside a browser reads forwards in time.
    void navigator.clipboard.writeText(calls.map(logLine).join('\n')).then(markCopied, () => {
      /* a denied clipboard is not worth an error state */
    })
  }, [calls, markCopied])

  return (
    <div className="flex h-full flex-col">
      {/* -------- the worker rail -------- */}
      <div className="flex flex-none flex-wrap items-center gap-1 border-b border-line/60 px-3 py-1.5">
        <WorkerRail
          workers={hunt.workers}
          counts={counts}
          selected={worker}
          onSelect={onSelectWorker}
        />

        {worker !== null && rows.length > 0 ? (
          <button
            type="button"
            onClick={onCopy}
            className="ml-auto rounded-xs px-1.5 text-[11px] text-accent hover:bg-s2 focus-visible:ring-1 focus-visible:ring-accent focus-visible:outline-none"
          >
            {copied ? 'copied ✓' : 'copy as text'}
          </button>
        ) : null}
      </div>

      {/* -------- the log -------- */}
      {worker === null ? (
        <div className="flex flex-1 items-center justify-center px-3 py-6 text-center text-[12px] text-faint">
          pick a worker above to see every call it has made
        </div>
      ) : cap === 0 ? (
        <div className="flex flex-1 items-center justify-center px-3 py-6 text-center text-[12px] text-faint">
          the per-worker log is switched off — set a depth in Settings
        </div>
      ) : hunt.logDegraded === true ? (
        /*
          Said out loud, not rendered as an empty list.

          An unreachable store and a worker that has made no calls produce the
          same empty array, and they mean opposite things — one is "nothing to
          see", the other is "you are not seeing it". The counters beside this
          are computed in the API process and stay correct, which is why the
          rest of the panel is still worth reading.
        */
        <div className="flex flex-1 flex-col items-center justify-center gap-1 px-3 py-6 text-center">
          <span className="text-[12px] text-warn">the log store is unreachable</span>
          <span className="text-[11px] text-faint">
            the hunt is unaffected and the counters above are live — only the stored calls are
            missing
          </span>
        </div>
      ) : (
        /*
          ONE scroll container, with the header sticky inside it.

          The header used to be a sibling above the scroller, which is simpler
          and wrong in a way that only shows up at depth: with two hundred rows
          the column titles are on screen for the first eight and gone for the
          rest, so every reading after that is done from memory. Sticky costs
          one compositor layer and nothing per frame.
        */
        <>
          {/*
            The caption sits OUTSIDE the table, not inside it as a sticky first
            child. `role="table"` accepts only rows and rowgroups; a bare div
            between the table and its rows drops the whole structure out of the
            accessibility tree, so the rows are announced as nothing at all.
          */}
          <div className="flex-none border-b border-line/60 px-3 py-1 text-[11px] text-faint">
            {tag(worker)} · {count(rows.length)} of the last {count(cap)} calls · newest first
            {hunt.live ? '' : ' · frozen at stop'}
          </div>

          {rows.length === 0 ? (
            <p className="px-3 py-2 text-[11px] text-faint">
              {owned ? `${tag(worker)} has not completed a call yet` : 'loading…'}
            </p>
          ) : (
            <div
              role="table"
              aria-label={`Calls made by worker ${worker}`}
              className="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
            >
              <div role="rowgroup" className="sticky top-0 z-10 bg-s1">
                <div
                  role="row"
                  className={cn(
                    LOG_COLS,
                    'border-b border-line/60 px-3 py-1 text-[10px] tracking-[0.06em] text-faint uppercase'
                  )}
                >
                  {LOG_HEAD.map((h) => (
                    <span key={h} role="columnheader" className="truncate">
                      {h}
                    </span>
                  ))}
                </div>
              </div>
              <div role="rowgroup">
                {rows.map((c, i) => (
                  // `prev` is the OLDER neighbour, which in a reversed list is
                  // the next index — that is what makes the gap column mean
                  // "since the previous call" rather than "until the next one".
                  <LogLine key={`${c.seq}-${c.at}`} c={c} prev={rows[i + 1]} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
})

/**
 * Pick a worker. One tab stop, arrow keys inside.
 *
 * Same reasoning as the console's filter chips: these are mutually exclusive, so
 * `radiogroup` is what says "one choice among five" — a row of independent
 * buttons announces five unrelated controls and never conveys that picking one
 * drops the others. Five extra tab stops in front of the log is also five extra
 * presses to reach it.
 *
 * Memoised on its own because it re-renders 1.4 times a second otherwise: the
 * counts move on every poll, and the rail is the one part of this tab whose
 * layout is expensive relative to its content.
 */
const WorkerRail = React.memo(function WorkerRail({
  workers,
  counts,
  selected,
  onSelect,
}: {
  workers: HuntLiveDto['workers']
  counts: readonly number[]
  selected: number | null
  onSelect: (worker: number | null) => void
}) {
  const present = workers.filter((w): w is NonNullable<typeof w> => w != null)
  const refs = React.useRef<Array<HTMLButtonElement | null>>([])

  const move = (from: number, delta: number): void => {
    const next = (from + delta + present.length) % present.length
    const w = present[next]
    if (!w) return
    refs.current[next]?.focus()
    onSelect(w.worker)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number): void => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault()
        move(index, 1)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault()
        move(index, -1)
        break
      case 'Home':
        e.preventDefault()
        move(0, 0)
        break
      case 'End':
        e.preventDefault()
        move(0, present.length - 1)
        break
      default:
        break
    }
  }

  return (
    <div role="radiogroup" aria-label="Worker" className="flex flex-wrap items-center gap-1">
      {present.map((w, index) => {
        const active = w.worker === selected
        const n = counts[w.worker] ?? 0
        return (
          <button
            key={w.worker}
            ref={(el) => {
              refs.current[index] = el
            }}
            type="button"
            role="radio"
            aria-checked={active}
            /*
             * Selection is the FIRST tab stop when nothing is chosen, so the
             * group is reachable before a worker has been picked — a roving
             * tabindex where no option is selected is a group nobody can enter.
             */
            tabIndex={active || (selected === null && index === 0) ? 0 : -1}
            aria-label={`${roleName(w.role)} worker ${w.worker}, ${n} calls${
              w.errors > 0 ? `, ${w.errors} refused` : ''
            }`}
            onKeyDown={(e) => onKeyDown(e, index)}
            onClick={() => onSelect(active ? null : w.worker)}
            className={cn(
              'tabular rounded-xs px-1.5 py-0.5 text-[11px]',
              'focus-visible:ring-1 focus-visible:ring-accent focus-visible:outline-none',
              active ? 'bg-accent-soft text-accent' : 'text-faint hover:bg-s2 hover:text-muted'
            )}
            title={`${w.egress} · ${w.accounts}/${w.accountsTotal} identities`}
          >
            {roleTag(w)}
            <span aria-hidden="true" className="ml-1 text-[10px] opacity-70">
              {count(n)}
            </span>
            {w.errors > 0 ? (
              <span aria-hidden="true" className="ml-1 text-[10px] text-warn">
                {w.errors}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
})

/* ------------------------------------------------------------------ *
 * Active hits — every sighting of an open shift
 * ------------------------------------------------------------------ */

/**
 * The sighting list's lens: one fleet, or all of them together.
 *
 * The retired job fleet has no chip. A run stored while it ran may still carry
 * sightings tagged `job`; they stay under All, and nothing arms that fleet again.
 */
type FleetFilter = 'all' | Exclude<WorkerRole, 'job'>

/**
 * The chips, in the order somebody reads them.
 *
 * `All` first because it is the default and the question most people arrive
 * with. Then the fleets in the order they were built, which is also the order
 * they appear in the shared worker index space, so the chips run left to right
 * the same way `w… s… ga…` does.
 *
 * `GraphQL` rather than `Shift-list` on this one strip: it is the label an
 * operator asked for, it names the endpoint the fleet actually calls, and it is
 * unambiguous next to `Schedule` in a way that "shift list" is not.
 */
const FLEET_CHIPS: readonly { id: FleetFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'watch', label: 'Schedule' },
  { id: 'shift-list', label: 'GraphQL' },
  /*
   * Last, and it will read zero on every claim hunt.
   *
   * The GA fleet fires no claims — it publishes an opening and the application
   * gate creates — so a sighting under this chip belongs to a customer who was
   * waiting to apply, not one who was racing for a seat. The two never appear in
   * the same run, so the chip is empty in whichever of the two you are looking
   * at, which is correct rather than missing.
   */
  { id: 'ga', label: 'GA' },
]

/**
 * A single-choice filter, as a real radio group.
 *
 * Same shape and the same reasoning as `ChipGroup` in `run-console.tsx`: these
 * are mutually exclusive, so `aria-pressed` would describe four independent
 * toggles and never convey that they are one choice. A radio group is ONE tab
 * stop with arrow keys moving inside it, which is why the roving tabindex and
 * the Arrow/Home/End handling below are not optional extras.
 */
const FleetChips = React.memo(function FleetChips({
  value,
  onChange,
  counts,
}: {
  value: FleetFilter
  onChange: (next: FleetFilter) => void
  counts: Record<FleetFilter, number>
}) {
  const refs = React.useRef<Array<HTMLButtonElement | null>>([])

  const move = (from: number, delta: number): void => {
    const next = (from + delta + FLEET_CHIPS.length) % FLEET_CHIPS.length
    const chip = FLEET_CHIPS[next]
    if (!chip) return
    refs.current[next]?.focus()
    onChange(chip.id)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number): void => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault()
        move(index, 1)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault()
        move(index, -1)
        break
      case 'Home':
        e.preventDefault()
        move(0, 0)
        break
      case 'End':
        e.preventDefault()
        move(0, FLEET_CHIPS.length - 1)
        break
      default:
        break
    }
  }

  return (
    <div
      role="radiogroup"
      aria-label="Which detector's sightings"
      className="flex flex-wrap items-center gap-0.5 rounded-md border border-line bg-s1 p-0.5"
    >
      {FLEET_CHIPS.map((chip, index) => {
        const active = chip.id === value
        const n = counts[chip.id]
        return (
          <button
            key={chip.id}
            ref={(el) => {
              refs.current[index] = el
            }}
            type="button"
            role="radio"
            aria-checked={active}
            /*
             * Spelled out for a screen reader, which would otherwise announce
             * "Schedule 1243" — two unrelated values read as one string.
             */
            aria-label={`${chip.label}, ${n} sighting${n === 1 ? '' : 's'}`}
            tabIndex={active ? 0 : -1}
            onKeyDown={(e) => onKeyDown(e, index)}
            onClick={() => onChange(chip.id)}
            className={cn(
              'flex h-6 items-center gap-1 rounded-sm px-2 text-[12px] whitespace-nowrap transition-colors',
              'focus-visible:ring-1 focus-visible:ring-accent focus-visible:outline-none',
              active ? 'bg-s3 text-fg' : 'text-muted hover:bg-s2 hover:text-fg'
            )}
          >
            <span>{chip.label}</span>
            {/*
              A fleet with nothing to show still carries its zero. An absent
              number reads as "not counted", and "this detector has never seen
              the shift open" is the single most useful thing this strip says.
            */}
            <span aria-hidden="true" className="tabular text-[10.5px] text-faint">
              {count(n)}
            </span>
          </button>
        )
      })}
    </div>
  )
})

const HIT_COLS =
  'grid min-w-[40.25rem] grid-cols-[3.4rem_6.2rem_2.25rem_7.5rem_minmax(5rem,1fr)_3.8rem_3.4rem_5rem] gap-x-2'
const HIT_HEAD = ['#', 'time', 'w', 'account id', 'login', 'rtt', 'ttfb', '']

/** One sighting as a text line, for pasting somewhere that is not a browser. */
export function hitLine(h: ActiveHitDto): string {
  return (
    `${new Date(h.at).toISOString().slice(11, 23)}  w${h.worker}  #${String(h.seq).padStart(6)}  ` +
    `${(h.accountId || '-').padEnd(26)}  ${h.account.padEnd(22)}  ` +
    `${String(h.ms).padStart(5)}ms  ttfb=${String(h.ttfbMs).padStart(4)}  ` +
    `${h.struck ? 'CLAIMED' : 'seen'}  ${h.egress}`
  )
}

/**
 * How far apart two sightings can be and still be the SAME opening.
 *
 * Workers catching one opening land within a few hundred milliseconds of each
 * other — one round trip, at most. A shift that closes and reopens is seconds or
 * minutes later. Two seconds sits in the empty space between those two scales,
 * so the grouping never has to be a judgement call.
 */
const SAME_OPENING_MS = 2_000

const HitLine = React.memo(function HitLine({
  h,
  first,
  onFocusWorker,
}: {
  h: ActiveHitDto
  /**
   * The earliest sighting of THIS opening.
   *
   * Not the earliest of the run: a shift that flickers produces several
   * openings, and measuring the second one's sightings against the first one's
   * clock would report a worker as "+94000ms behind" when it was in fact first.
   */
  first: number
  onFocusWorker: (worker: number) => void
}) {
  const tag = useWorkerTag()
  const behind = h.at - first
  return (
    <div
      role="row"
      className={cn(
        HIT_COLS,
        'tabular items-center border-b border-line/25 px-3 py-1 text-[11px] last:border-0',
        h.struck && 'bg-ok-soft/25'
      )}
      style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 24px' }}
    >
      <span role="cell" className="text-faint">
        #{h.seq}
      </span>
      {/*
        Milliseconds, always. The whole event is tens of them wide: several
        workers see the same opening within a few frames of each other, and a
        timestamp truncated to the second would render them all identical.
      */}
      <span role="cell" className="text-fg">
        {stamp(h.at)}
      </span>
      <span role="cell">
        <button
          type="button"
          onClick={() => onFocusWorker(h.worker)}
          aria-label={`Focus ${roleName(h.role)} worker ${h.worker}`}
          className="rounded-xs text-left text-fg hover:text-accent focus-visible:ring-1 focus-visible:ring-accent focus-visible:outline-none"
        >
          {/*
            The role the server stamped, wherever the row carries one — see
            `roleTag`. The arithmetic fallback is for sightings recorded before
            that field shipped, and for those `watch` is what the whole fleet was.
          */}
          {h.role === undefined ? tag(h.worker) : roleTag({ worker: h.worker, role: h.role })}
        </button>
      </span>
      <span
        role="cell"
        className="truncate text-faint"
        title={h.accountId || 'the customer’s own session'}
      >
        {shortId(h.accountId)}
      </span>
      <span role="cell" className="truncate text-accent/80" title={h.account}>
        {shortAccount(h.account)}
      </span>
      <span role="cell" className={rttTone(h.ms)}>
        {h.ms > 0 ? `${h.ms}ms` : '—'}
      </span>
      <span role="cell" className="text-faint">
        {h.ttfbMs > 0 ? `${h.ttfbMs}` : '—'}
      </span>
      {/*
        Exactly one sighting per opening fires the claim; the others are workers
        that read the same document a few milliseconds later, by which point the
        update was already on the wire. `+12ms` is that lateness — and it is the
        honest measure of how much spreading the watch across five addresses is
        actually buying.
      */}
      <span
        role="cell"
        className={h.struck ? 'rounded-xs bg-ok-soft px-1 font-medium text-ok' : 'text-faint'}
        title={
          h.struck
            ? 'This sighting fired the claim'
            : `Saw the same opening ${behind}ms after the worker that claimed it`
        }
      >
        {h.struck ? 'claimed' : behind > 0 ? `+${behind}ms` : 'seen'}
      </span>
    </div>
  )
})

/**
 * Every time a worker read ACTIVE off the schedule.
 *
 * ── Why this is not a filter on the Log tab ─────────────────────────
 * The log is per-worker and capped at a couple of hundred calls, which at
 * thirteen checks a second is under a minute. The sighting that decided a run is
 * out of it before anyone can open the drawer, and it is the one record that
 * must never be evicted — so these are kept separately, across all workers, for
 * the life of the run.
 *
 * Reading down the list answers the question the rest of the console cannot:
 * whether the shift opened at all, how many workers caught it, how far apart
 * they were, which identity was carrying the check that won, and whether the
 * claim went out on the first sighting or a later one.
 */
export const ActiveHitsTab = React.memo(function ActiveHitsTab({
  hunt,
  onFocusWorker,
}: {
  hunt: HuntLiveDto
  onFocusWorker: (worker: number) => void
}) {
  const [copied, markCopied] = useCopiedFlag()
  const [fleet, setFleet] = React.useState<FleetFilter>('all')
  const { watch, gql } = useWorkerFleets()
  const hits = hunt.activeHits ?? []

  /*
   * Grouped into openings, tagged with a fleet, then reversed — newest first,
   * like every other live list here.
   *
   * The grouping walks forwards in time because that is the direction the
   * question runs in: an opening starts with whichever worker saw it first, and
   * everything after it within a couple of seconds is another worker seeing the
   * same thing.
   *
   * It runs over EVERY sighting, before any filter. "+94ms behind" means behind
   * whoever saw this opening first, and that worker is very often on another
   * fleet — measuring lateness inside a filtered list would report the first row
   * of each sub-tab as the one that was first, which is precisely the comparison
   * between detectors this tab exists to make.
   */
  const rows = React.useMemo(() => {
    const out: { h: ActiveHitDto; first: number; role: WorkerRole }[] = []
    let first = 0
    let previous = 0
    for (const h of hits) {
      if (first === 0 || h.at - previous > SAME_OPENING_MS) first = h.at
      previous = h.at
      out.push({ h, first, role: h.role ?? fleetRole(h.worker, watch, gql) })
    }
    return out.reverse()
  }, [hits, watch, gql])

  const shown = React.useMemo(
    () => (fleet === 'all' ? rows : rows.filter((r) => r.role === fleet)),
    [rows, fleet]
  )

  /*
   * The badges count the RUN, not the rows on screen.
   *
   * `activesByRole` is the server's own running tally. Counting the loaded rows
   * instead would give badges that climb as the console catches up with a long
   * run, and the fallback below does exactly that — it is only for a payload
   * that predates the field, where the rows are all there is.
   */
  const loaded = React.useMemo(() => {
    // `job` stays in the tally so a row stored with that role still counts —
    // under All — rather than throwing on a key the map lacks.
    const n: Record<WorkerRole, number> = { watch: 0, 'shift-list': 0, job: 0, ga: 0 }
    for (const r of rows) n[r.role] += 1
    return n
  }, [rows])
  const byRole: Partial<Record<WorkerRole, number>> = hunt.activesByRole ?? loaded
  const total = hunt.actives ?? hits.length
  /*
   * How many the chosen fleet saw, whole-run.
   *
   * `all` takes `actives` rather than the sum of the fleets: the sum is only
   * equal while every sighting carries a role, and a run that started before
   * that field shipped would quietly report zero across the board. A fleet the
   * server did not tally reads 0, not undefined.
   */
  const scopeTotal = fleet === 'all' ? total : (byRole[fleet] ?? 0)
  const workers = new Set(shown.map((r) => r.h.worker)).size

  const onCopy = React.useCallback(() => {
    void navigator.clipboard
      // What is on screen, filter and all. Copying rows the reader cannot see
      // would paste a different list from the one they are looking at.
      .writeText(
        shown
          .map((r) => r.h)
          .reverse()
          .map(hitLine)
          .join('\n')
      )
      .then(markCopied, () => {
        /* a denied clipboard is not worth an error state */
      })
  }, [shown, markCopied])

  if (hits.length === 0) {
    return (
      <div className="px-3 py-6 text-center">
        <div className="text-[12px] text-muted">
          {hunt.activeDegraded
            ? 'the sighting store could not be read'
            : total > 0
              ? 'sightings were counted before this build started keeping them'
              : 'No worker has seen ACTIVE yet'}
        </div>
        <div className="mt-1 text-[11px] text-faint">
          {hunt.activeDegraded
            ? `${count(total)} sighting${total === 1 ? '' : 's'} were counted — the records that name them are stored, but could not be fetched. This is a store problem, not a quiet fleet.`
            : hunt.live
              ? `${hunt.workers.length} workers are reading the schedule ${hunt.rate}/s. The instant one of them reads ACTIVE it lands here — with the identity it asked as, and to the millisecond.`
              : 'This run ended without the shift ever opening.'}
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      {/*
        The fleets, as a filter over one list rather than three separate lists.

        The schedule workers read the schedule and are the only fleet that
        claims; the GA fleet reads the job's list before there is anything to
        claim, and a run stored before the job fleet was retired may still carry
        its sightings. Mixed together they answer "did the shift open"; split
        apart they answer the question that decides configuration — which fleet
        is actually finding shifts, and how far behind the others are.
        Neither reading is available without the other, so the rows stay one
        list and this chooses the lens.
      */}
      <div className="flex flex-none items-center gap-2 border-b border-line/60 px-3 py-1.5">
        <FleetChips
          value={fleet}
          onChange={setFleet}
          counts={{
            all: total,
            watch: byRole.watch ?? 0,
            'shift-list': byRole['shift-list'] ?? 0,
            ga: byRole.ga ?? 0,
          }}
        />
      </div>

      <div className="tabular flex flex-none flex-wrap items-center gap-x-3 gap-y-1 border-b border-line/60 px-3 py-1.5 text-[11px]">
        <span className="text-fg">
          {count(scopeTotal)} sighting{scopeTotal === 1 ? '' : 's'}
          {fleet === 'all' ? '' : ` from ${roleName(fleet).toLowerCase()}`}
        </span>
        <span className="text-faint">
          across {workers} of {hunt.workers.length} workers
        </span>
        {/*
          Said only when the list is not the whole of what it describes — a store
          that could not be read, or a run long enough that the first page did
          not reach the beginning. Every sighting is kept, so in the ordinary
          case there is nothing to disclose and this stays quiet.
        */}
        {hunt.activeDegraded ? (
          <span className="rounded-xs bg-warn-soft px-1.5 text-warn">
            store unreadable · showing {count(shown.length)} held in memory
          </span>
        ) : shown.length < scopeTotal ? (
          <span className="text-faint">· showing the newest {count(shown.length)}</span>
        ) : null}
        {/*
          The claim is stated here rather than left to be found in the list. It
          is the only row that answers the question the tab is opened with.
        */}
        {hunt.claim ? (
          <span className="rounded-xs bg-ok-soft px-1.5 text-ok">
            claimed at {stamp(hunt.claim.at)}
          </span>
        ) : rows.some((r) => r.h.struck) ? (
          <span className="rounded-xs bg-warn-soft px-1.5 text-warn">claim attempted, not won</span>
        ) : null}
        <button
          type="button"
          onClick={onCopy}
          className="ml-auto rounded-xs px-1.5 text-accent hover:bg-s2 focus-visible:ring-1 focus-visible:ring-accent focus-visible:outline-none"
        >
          {copied ? 'copied ✓' : 'copy as text'}
        </button>
      </div>

      {fleet !== 'all' && shown.length === 0 ? (
        /*
          An empty FLEET, not an empty run — and the difference is the finding.
          A detector that has never seen an opening the others caught is either
          misconfigured or reading something else, and it must not look like a
          quiet afternoon.

          Written as "a fleet is selected AND it is empty" rather than on the
          count alone: `all` cannot be empty here — the whole component returned
          early when there were no sightings at all — and saying so is what lets
          the label below be a fleet name rather than a maybe-fleet name.
        */
        <div className="px-3 py-6 text-center">
          <div className="text-[12px] text-muted">
            No sighting from {roleName(fleet).toLowerCase()}
          </div>
          <div className="mt-1 text-[11px] text-faint">
            {count(total)} sighting{total === 1 ? '' : 's'} came from the other detectors. A fleet
            that never sees an opening its siblings caught is reading the wrong thing, or is not
            reading at all.
          </div>
        </div>
      ) : (
        /* Header sticky INSIDE the scroller, for the same reason as the call log:
           column titles that leave the screen turn every later row into a guess. */
        <div
          role="table"
          aria-label="Sightings of ACTIVE"
          className="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
        >
          <div role="rowgroup" className="sticky top-0 z-10 bg-s1">
            <div
              role="row"
              className={cn(
                HIT_COLS,
                'border-b border-line/60 px-3 py-1 text-[10px] tracking-[0.06em] text-faint uppercase'
              )}
            >
              {HIT_HEAD.map((h, i) => (
                <span key={h === '' ? `blank-${i}` : h} role="columnheader" className="truncate">
                  {h === '' ? <span className="sr-only">outcome</span> : h}
                </span>
              ))}
            </div>
          </div>

          <div role="rowgroup">
            {shown.map(({ h, first }) => (
              <HitLine
                key={`${h.at}-${h.worker}-${h.seq}`}
                h={h}
                first={first}
                onFocusWorker={onFocusWorker}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
})

/* ------------------------------------------------------------------ *
 * Accounts tab
 * ------------------------------------------------------------------ */

const ACC_COLS =
  'grid min-w-[37.5rem] grid-cols-[minmax(8rem,1.6fr)_2.2rem_3.2rem_3rem_3.4rem_3.4rem_3.4rem_3.6rem_3.2rem] gap-x-2'
const ACC_HEAD = ['identity', 'w', 'checks', 'ok', '429', '401/403', 'other', 'refused', 'ttfb']

/**
 * How hard one identity is being worked, relative to the busiest.
 *
 * The bar is the finding, not decoration. A per-caller rate limit shows up as a
 * single long bar with a red refusal share next to four short green ones — and
 * that shape is legible in a glance in a way that nine columns of numbers is
 * not, which matters when the question is being asked under time pressure.
 */
const AccountLine = React.memo(function AccountLine({
  a,
  busiest,
  onFocusWorker,
}: {
  a: AccountStatDto
  busiest: number
  onFocusWorker: (worker: number) => void
}) {
  const tag = useWorkerTag()
  const e429 = a.byStatus['429'] ?? 0
  const e401 = (a.byStatus['401'] ?? 0) + (a.byStatus['403'] ?? 0)
  const other = a.errors - e429 - e401
  const share = a.errors === 0 ? 0 : a.errors / a.checks
  const load = busiest === 0 ? 0 : a.checks / busiest
  const ttfb = a.ttfbN === 0 ? 0 : Math.round(a.ttfbTotal / a.ttfbN)

  return (
    <div className="border-b border-line/25 px-3 py-1 last:border-0">
      <div className={cn(ACC_COLS, 'tabular items-center text-[11px]')}>
        {/*
          Struck off AND labelled with why.

          The strike-through already said "no longer in a ring", which is the
          less useful half: `expiring` and `http 401` look identical struck
          through and are opposite findings — the pool handing out short-lived
          rows, versus the server refusing this identity. One is a claim-query
          problem and the other is an account problem, and the far more common
          one was the invisible one.
        */}
        <span
          className={cn(
            'flex min-w-0 items-center gap-1',
            a.live ? 'text-accent/80' : 'text-faint'
          )}
          title={`${a.account}${
            a.live ? ' — still in its ring' : ` — out of its ring: ${a.retiredFor ?? 'reason not recorded'}`
          }`}
        >
          <span className={cn('truncate', a.live ? '' : 'line-through')}>
            {shortAccount(a.account)}
          </span>
          {a.live ? null : (
            <span
              className={cn(
                'shrink-0 rounded-xs px-1 text-[9.5px] whitespace-nowrap',
                // Expiry is housekeeping; a refusal is a finding. Toned apart so
                // a column of one is not mistaken for a column of the other.
                a.retiredFor?.startsWith('http') ? 'bg-warn-soft text-warn' : 'bg-s3 text-faint'
              )}
            >
              {a.retiredFor ?? 'retired'}
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={() => onFocusWorker(a.worker)}
          className="text-left text-fg hover:text-accent"
        >
          {tag(a.worker)}
        </button>
        <span className="text-fg">{count(a.checks)}</span>
        <span className="text-ok">{count(a.ok)}</span>
        <span className={e429 > 0 ? 'text-danger' : 'text-faint'}>{e429 === 0 ? '—' : count(e429)}</span>
        <span className={e401 > 0 ? 'text-warn' : 'text-faint'}>{e401 === 0 ? '—' : count(e401)}</span>
        <span className={other > 0 ? 'text-warn' : 'text-faint'}>{other <= 0 ? '—' : count(other)}</span>
        <span className={share >= 0.05 ? 'text-danger' : 'text-faint'}>
          {a.errors === 0 ? '—' : `${(share * 100).toFixed(1)}%`}
        </span>
        <span className="text-faint">{ttfb === 0 ? '—' : `${ttfb}ms`}</span>
      </div>
      {/* Load bar: how much of the busiest identity's traffic this one carries,
          with the refused share drawn inside it. */}
      <div className="mt-0.5 flex h-[3px] overflow-hidden rounded-full bg-s3">
        <div
          className={cn('h-full', share >= 0.05 ? 'bg-danger/70' : 'bg-accent/50')}
          style={{ width: `${Math.max(1, load * 100).toFixed(1)}%` }}
        />
      </div>
    </div>
  )
})

export const AccountsTab = React.memo(function AccountsTab({
  hunt,
  onFocusWorker,
}: {
  hunt: HuntLiveDto
  onFocusWorker: (worker: number) => void
}) {
  const tag = useWorkerTag()
  const rows = hunt.accounts_detail ?? []
  const busiest = rows.length === 0 ? 0 : Math.max(...rows.map((a) => a.checks))

  const swaps = hunt.accountSwaps ?? 0
  /*
   * Measured against the hunt's own elapsed time, and guarded against dividing
   * by a fraction of a second: a run four seconds old with one swap is not doing
   * fifteen a minute, it has done one thing. Below half a minute the rate is
   * simply not shown.
   */
  const elapsedMin = Math.max(0, hunt.elapsedMs) / 60_000
  const churnPerMin = elapsedMin >= 0.5 ? swaps / elapsedMin : 0

  /*
   * The verdict, stated rather than left to be inferred.
   *
   * This is the whole reason the tab exists: the numbers below prove which of
   * two indistinguishable failures is happening, and an operator should not have
   * to reconstruct the argument from a table every time. If the identity
   * carrying the most requests is also collecting essentially all of the
   * refusals, the limit follows the CALLER and more accounts is the only fix.
   */
  const verdict = React.useMemo(() => {
    if (rows.length < 2) return null
    const worst = rows.reduce((a, b) => (b.errors > a.errors ? b : a))
    if (worst.errors < 10) return null
    const totalErrors = rows.reduce((n, a) => n + a.errors, 0)
    if (worst.errors / totalErrors < 0.6) return null
    const median = [...rows].sort((a, b) => a.checks - b.checks)[Math.floor(rows.length / 2)]
    const ratio = median && median.checks > 0 ? worst.checks / median.checks : 1
    return {
      account: worst.account,
      worker: worst.worker,
      pct: Math.round((worst.errors / totalErrors) * 100),
      ratio: ratio.toFixed(1),
    }
  }, [rows])

  /*
   * The shift-list fleet is absent from this table BY DESIGN, so say so.
   *
   * It borrows no identity — the endpoint is unauthenticated — which is why
   * the server keeps it out of the identity accounting entirely: this tab's
   * verdict advises giving a worker more accounts, and that is impossible
   * advice for a worker that cannot hold one. Unsaid, though, a hunt running
   * five searchers and no watchers renders an empty tab that reads as a
   * failure to record anything.
   */
  const searchers = hunt.gql?.workers ?? 0
  const searchNote =
    searchers === 0 ? null : (
      <div className="flex-none border-b border-line/60 px-3 py-1 text-[11px] text-faint">
        {searchers} shift-list worker{searchers === 1 ? '' : 's'} are not listed here — they call
        an unauthenticated endpoint and borrow no identity. Their calls are on the Search tab.
      </div>
    )

  if (rows.length === 0) {
    return (
      <div className="flex h-full flex-col">
        {searchNote}
        <div className="flex flex-1 items-center justify-center px-3 py-6 text-center text-[12px] text-faint">
          no borrowed identities have been used yet
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      {searchNote}
      {verdict ? (
        <div className="flex-none border-b border-line/60 bg-warn-soft/20 px-3 py-1.5 text-[11.5px] text-warn">
          <TriangleAlert aria-hidden="true" className="mr-1 inline size-3.5 shrink-0 align-[-2px]" />
          <span className="font-medium">{shortAccount(verdict.account)}</span> ({tag(verdict.worker)}) is
          carrying {verdict.pct}% of all refusals and {verdict.ratio}× the median identity&rsquo;s
          requests — the limit is following the CALLER, not the address, so rotating its endpoint
          will not help. Give {tag(verdict.worker)} more accounts.
        </div>
      ) : null}

      {/*
        Churn is stated as a RATE, because the count alone cannot be judged.

        "25 held, 41 seen" is unremarkable over an hour and a fault in two
        minutes, and the table below shows only the identities — never how fast
        they are being cycled. Each swap is three writes against a collection the
        sibling system shares, so a run replacing an identity every few seconds
        is doing real work nobody asked for.

        Flagged past one swap a minute per worker, which is roughly the point
        where the cause stops being ordinary expiry and starts being the pool
        handing out rows that were never worth claiming.
      */}
      {swaps > 0 ? (
        <div
          className={cn(
            'flex-none border-b border-line/60 px-3 py-1 text-[11px]',
            churnPerMin > hunt.workers.length ? 'bg-warn-soft/20 text-warn' : 'text-faint'
          )}
        >
          {count(swaps)} identity swap{swaps === 1 ? '' : 's'} so far
          {elapsedMin >= 0.5 ? ` · ${churnPerMin.toFixed(1)}/min` : ''}
          {churnPerMin > hunt.workers.length
            ? ' — the pool is handing out accounts with little life left; each swap costs three writes on a shared collection'
            : ''}
        </div>
      ) : null}

      <div className="flex-none border-b border-line/60 px-3 py-1.5 text-[11px] text-faint">
        {rows.length} identities · busiest first · a long bar with a red share is a throttled caller
      </div>

      {/* Same shape as the checks tab: header and body share one horizontal
          scroller so they cannot fall out of column alignment. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-x-auto">
        <div
          className={cn(
            ACC_COLS,
            'flex-none border-b border-line/60 px-3 py-1 text-[10px] tracking-[0.06em] text-faint uppercase'
          )}
        >
          {ACC_HEAD.map((h, i) => (
            <span key={i} className="truncate">
              {h}
            </span>
          ))}
        </div>

        <div className="min-h-0 min-w-[37.5rem] flex-1 overflow-y-auto">
          {rows.map((a) => (
            <AccountLine
              key={a.account}
              a={a}
              busiest={busiest}
              onFocusWorker={onFocusWorker}
            />
          ))}
        </div>
      </div>
    </div>
  )
})

/* ------------------------------------------------------------------ *
 * Overview tab
 * ------------------------------------------------------------------ */

export const OverviewTab = React.memo(function OverviewTab({
  hunt,
  digest,
  focus,
  onFocus,
  onOpenErrors,
}: {
  hunt: HuntLiveDto
  digest: Digest
  focus: number | null
  onFocus: (worker: number) => void
  onOpenErrors: () => void
}) {
  const tag = useWorkerTag()
  const arming = hunt.live && digest.reported === 0
  const dash = (v: number, s: string) => (v > 0 ? s : '—')
  /*
   * Blank rather than absent on an older payload.
   *
   * A retained snapshot taken before this shipped has no `stats`, and reading
   * through to `undefined` would render `NaN ms` — which looks like a broken
   * number rather than an absent one.
   */
  const stats = hunt.stats ?? { n: 0, cap: 0, okN: 0, okAvgMs: 0, s403: 0, s405: 0, s429: 0 }
  const run = stats.run ?? { n: 0, okN: 0, okAvgMs: 0, s403: 0, s405: 0, s429: 0, other: 0 }
  /*
   * What the windowed tiles actually cover, said out loud.
   *
   * "last 500" once the ring has filled, and the honest smaller number while it
   * is still filling — a fresh hunt claiming to average 500 calls after twenty
   * would be a lie, and a quiet one.
   */
  const span = stats.n >= stats.cap ? `last ${count(stats.cap)}` : `last ${count(stats.n)}`

  /*
   * Summed from the worker rows when the top-level total is absent.
   *
   * A snapshot retained before this shipped has neither, and both fall back to
   * zero — which is honest, because that run genuinely has no record of a
   * sighting either way.
   */
  const actives = hunt.actives ?? hunt.workers.reduce((n, w) => n + (w?.actives ?? 0), 0)
  const activeWorkers = hunt.workers.reduce((n, w) => n + ((w?.actives ?? 0) > 0 ? 1 : 0), 0)

  return (
    <div className="h-full overflow-y-auto">
      <div role="group" aria-label="Hunt summary" className={cn(TILE_GRID, 'px-3 py-2')}>
        {/* `—`, never `0/s`. Zero reads as "it broke"; a dash reads as "there
            is no now" — which is the truth about a finished hunt. */}
        <Tile label="rate" value={hunt.live ? `${hunt.rate}/s` : '—'} />
        <Tile label="checks" value={count(hunt.polls)} />
        {/*
          The one number the whole run is for.

          A precise total across every worker, not a flag and not a de-duplicated
          "an opening happened" — several workers catching the same opening is
          itself the finding, and so is a shift that flickers. Counted in the
          same place as the per-worker column beneath it, from one condition, so
          the tile and the table can never disagree.

          Whole-run and never windowed: a sighting from two minutes ago is the
          most important thing that has happened, and the 500-call window would
          have dropped it after forty seconds.
        */}
        <Tile
          label="active seen"
          value={count(actives)}
          sub={actives === 0 ? 'whole run' : `across ${activeWorkers} worker${activeWorkers === 1 ? '' : 's'}`}
          title="Every time any worker read ACTIVE off the schedule, for the whole run. Openings are what the workers exist to catch, so this is never windowed. Open the Active tab for each one with its identity and timestamp."
          tone={actives === 0 ? 'neutral' : 'ok'}
        />
        <Tile
          /*
            Short enough to fit the tile it is in.
            "workers at stop" truncated to "WORKERS AT …", which reads as a
            broken label rather than a shortened one — and the tile below it
            already says the run is a post-mortem. The `sub` carries the
            qualifier instead, where there is room for it.
          */
          label="workers"
          sub={hunt.live ? 'reporting' : 'at stop'}
          value={`${digest.reported} / ${hunt.workers.length}`}
          tone={
            hunt.workers.length === 0
              ? 'neutral'
              : digest.reported === hunt.workers.length
                ? 'ok'
                : arming
                  ? 'neutral'
                  : 'warn'
          }
        />
        {/*
          An AVERAGE over the last 500 calls, computed on the server.

          The old tile was a median over `recent` — thirty records shared across
          every worker, which is under two and a half seconds at five gapless
          workers. That is not a statistic, it is the last thing that happened:
          it swung on a single slow check, and the refusal tiles beside it could
          not show a 429 from four seconds earlier. Five hundred is about forty
          seconds, and the totals slide with the window rather than being
          re-reduced per poll — so the width costs nothing.
        */}
        <Tile
          label="ok avg"
          value={dash(stats.okAvgMs, `${stats.okAvgMs} ms`)}
          sub={span}
          title={`Mean round-trip of the ${count(stats.okN)} accepted checks in the window. Refusals are excluded — they come back fast and would flatter it. Green to ${RTT_NORMAL_MS}ms, amber to ${RTT_SLOW_MS}ms, red past it.`}
          /*
           * The SAME bands as the worker rows beneath it.
           *
           * This tile is the mean of the very numbers in that table and was
           * scored on a different scale — so a column of 310–326ms read amber
           * while their own average read red. Two thresholds for one measurement
           * is not a styling detail; it is the summary disagreeing with the
           * evidence directly below it.
           */
          tone={rttToneKey(stats.okAvgMs)}
        />
        <Tile
          label="identities"
          value={`${hunt.accounts} / ${hunt.workers.reduce((n, w) => n + (w?.accountsTotal ?? 0), 0)}`}
          tone={hunt.accounts === 0 ? 'warn' : 'neutral'}
        />
        <Tile
          label="refused"
          value={count(hunt.errors)}
          sub="whole run"
          title="Every refusal since this hunt started. Only ever climbs — the three beside it cover the recent window and fall again once a cause is fixed."
          tone={hunt.errors === 0 ? 'ok' : 'warn'}
        />
        {/*
          The three refusals, over the SAME window as the average above — with
          the whole-run total underneath.

          Showing only the window is what made this confusing: a 429 from a
          minute ago is genuinely gone from it, so `refused 1` next to `429 —`
          read as a tally that had lost one. Both spans, both labelled, and the
          tile keeps its colour when EITHER is non-zero — a refusal that has
          aged out is still a refusal that happened.
        */}
        <StatusTile label="403" now={stats.s403} run={run.s403} span={span} tone="danger" />
        <StatusTile label="405" now={stats.s405} run={run.s405} span={span} tone="warn" />
        <StatusTile label="429" now={stats.s429} run={run.s429} span={span} tone="warn" />
      </div>

      {/*
        The second detector, in its own strip with its own heading.

        Not mixed into the tiles above, and the separation is the whole point:
        every figure up there describes schedule reads, which is what the watch is
        tuned against, and folding a different endpoint's counts into them would
        quietly change what each existing tile means. Rendered only when the
        detector is on, so a customer running the watch alone sees exactly the
        panel they saw before.
      */}
      {hunt.gql != null && hunt.gql.workers > 0 ? (
        <>
          <div className="flex flex-wrap items-center gap-x-2 px-3 pt-2 pb-0.5">
            <span className="text-[10px] font-medium tracking-[0.07em] text-faint uppercase">
              searching the shift list
            </span>
            <span className="text-[10px] text-faint">
              {hunt.gql.workers} worker{hunt.gql.workers === 1 ? '' : 's'} · unauthenticated · own
              ISP and fingerprint each
            </span>
          </div>
          <div role="group" aria-label="Shift-list summary" className={cn(TILE_GRID, 'px-3 pb-2')}>
            <Tile label="searches" value={count(hunt.gql.polls)} />
            {/*
              The tile that separates a working detector from one that can never
              fire. 200 OK in 180ms with zero cards reads as perfect coverage in
              every other number on this page, and means the job id, the filters
              or the query are wrong. The high-water mark carries its own warning:
              a run that has reached the page size may be watching a schedule
              sitting on a page nobody asks for.
            */}
            <Tile
              label="cards"
              value={hunt.gql.polls === 0 ? '—' : String(hunt.gql.lastCards)}
              sub={hunt.gql.cardsMax > 0 ? `max ${hunt.gql.cardsMax}` : 'last search'}
              title="Schedule cards in the most recent answer. Zero means the job id or the query returns nothing and the detector cannot fire — which looks identical to a job that is simply advertising no shifts. 1000 is the page size, so a schedule beyond it would never be seen; paging is deliberately not done."
              tone={
                hunt.gql.polls === 0
                  ? 'neutral'
                  : hunt.gql.lastCards === 0
                    ? 'danger'
                    : hunt.gql.cardsMax >= GQL_PAGE_SIZE
                      ? 'warn'
                      : 'ok'
              }
            />
            {/*
              The detector's whole point, so it carries WHEN as well as how many.
              A `found` of 40 reads as healthy whether the last one was four
              seconds or forty minutes ago, and those are opposite situations that
              the total alone cannot tell apart.
            */}
            <Tile
              label="found"
              value={count(hunt.gql.hits)}
              sub={
                hunt.gql.hits === 0 || hunt.gql.hitAgeMs == null
                  ? 'whole run'
                  : `last ${duration(hunt.gql.hitAgeMs)} ago`
              }
              title="Times a search found this customer's schedule on the job's shift list. Each one fires the same claim an ACTIVE sighting does."
              tone={hunt.gql.hits === 0 ? 'neutral' : 'ok'}
            />
            <Tile
              label="refused"
              value={count(hunt.gql.errors)}
              sub="whole run"
              title="Searches the endpoint refused, plus every call that got no reply at all. Open the Search tab and pick the worker to see what came back."
              tone={hunt.gql.errors === 0 ? 'ok' : 'warn'}
            />
            {/*
              The cheap failures, named as cheap. A run with many retries and no
              rotations is a healthy pool meeting a wobbly origin, and without
              this tile that is indistinguishable from one burning addresses.
            */}
            <Tile
              label="retried"
              value={count(hunt.gql.transients)}
              sub="no pool cost"
              title="429s, 408s and 5xx ridden out on the same caller. An origin having a bad moment says nothing about who is asking, so no address is spent on it."
              tone="neutral"
            />
            <Tile
              label="callers replaced"
              value={count(hunt.gql.rotations)}
              sub="45s pool cost each"
              title="Times a refusal replaced the whole caller — address and fingerprint together, since with no account and no token those two ARE the caller. Each replaced address goes back flagged refused, which benches that pool row for 45 seconds across this system and the sibling one."
              tone={hunt.gql.rotations === 0 ? 'ok' : 'warn'}
            />
            {/*
              Shown only when non-zero, and it is the tile that answers "why is it
              not rotating".

              A rotation the pool declines leaves the worker on an address it has
              already decided against, and every decline path is silent — so a
              stranded worker and a healthy one read identically in every other
              number here. Large beside a small "callers replaced" means the
              remedy is being asked for and refused: a pool problem, not a
              detector one.
            */}
            {hunt.gql.stuck > 0 ? (
              <Tile
                label="stuck"
                value={count(hunt.gql.stuck)}
                sub="pool had none"
                title="Replacements that were asked for and did not happen, because the pool had no address to give. The worker stayed on an endpoint it had already given up on. If this is climbing, the pool is exhausted."
                tone="danger"
              />
            ) : null}
            {/*
              Last, and only when it has fired. It means the endpoint is writing
              schedule ids in a shape the match no longer recognises — a detector
              that has quietly stopped being able to see its own target, which is
              the failure that looks healthiest in every counter beside it.
            */}
            {hunt.gql.drift ? (
              <Tile
                label="id shape"
                value="drift"
                sub="not matching"
                title="The schedule's digits appeared in a response without its qualified id, so the shift list is writing ids in a shape this build does not recognise. Nothing is claimed on a partial match — job ids and schedule ids share a shape, so it could fire against the wrong one. Open the Search tab and read a response body."
                tone="danger"
              />
            ) : null}
            {/*
              Separate from "refused", and that separation is the whole point.

              A refusal is the transport rejecting the caller, and the remedy is a
              new address — which is what this fleet does automatically. This is
              the endpoint ACCEPTING the request and rejecting the query, at HTTP
              200, which no address can fix and which counted as "no cards" until
              it was given its own name. Any number above zero means some of what
              looked like an idle job never was.
            */}
            {hunt.gql.graphErrors > 0 ? (
              <Tile
                label="query rejected"
                value={count(hunt.gql.graphErrors)}
                sub="200 with errors"
                title={
                  hunt.gql.graphError ??
                  'The endpoint answered 200 carrying GraphQL errors instead of cards.'
                }
                tone="danger"
              />
            ) : null}
          </div>
        </>
      ) : null}

      <div className="flex flex-wrap items-center gap-x-2 px-3 pt-1 pb-0.5">
        <span className="text-[10px] font-medium tracking-[0.07em] text-faint uppercase">
          {hunt.live ? 'workers' : 'workers as they stopped'}
        </span>
        {/*
          Which columns cover WHAT, because this table mixes three spans.

          `checks` and `err` are whole-run totals, `403/405/429` and `avg` cover
          the recent window, and `last`/`rtt`/`ago` describe one call. Unlabelled,
          a worker showing `err 1` beside `429 –` reads as a contradiction — the
          refusal is simply older than the window.
        */}
        <span className="text-[10.5px] text-faint">
          · checks &amp; err are whole-run · 403/405/429 &amp; avg are the {span}
        </span>
        <span className="ml-auto text-[10.5px] text-faint">click a worker to focus every tab</span>
      </div>

      <div className="overflow-x-auto px-3 pb-1">
        <div
          className={cn(
            WORKER_COLS,
            'pb-0.5 text-[10px] tracking-[0.06em] text-faint uppercase'
          )}
        >
          {WORKER_HEAD.map((h, i) => (
            <span key={i} className="truncate">
              {h}
            </span>
          ))}
        </div>
        {hunt.workers.map((w) =>
          w == null ? null : (
            <WorkerLine
              key={w.worker}
              w={w}
              tone={digest.health[w.worker] ?? 'ok'}
              prefix={digest.egressPrefix}
              focused={focus === w.worker}
              arming={arming}
              onFocus={onFocus}
            />
          )
        )}
        {digest.egressPrefix === '' ? null : (
          <p className="pt-0.5 text-right text-[10.5px] text-faint">
            all on {digest.egressPrefix}
          </p>
        )}
      </div>

      {digest.verdicts.length > 0 ? (
        <div className="space-y-0.5 border-t border-line/60 px-3 py-1.5">
          {digest.verdicts.map((v, i) => (
            <p
              key={i}
              className={cn(
                'flex items-start gap-1.5 text-[11.5px]',
                v.tone === 'danger' ? 'text-danger' : 'text-warn'
              )}
            >
              <TriangleAlert aria-hidden="true" className="mt-px size-3.5 shrink-0" />
              <span>
                <span className="font-medium">{tag(v.worker)}</span>{' '}
                {hunt.live ? v.text : v.pastText}
              </span>
            </p>
          ))}
        </div>
      ) : null}

      {hunt.lastError ? (
        <button
          type="button"
          onClick={onOpenErrors}
          className="tabular flex w-full items-center gap-2 border-t border-line/60 bg-warn-soft/20 px-3 py-1.5 text-left text-[11px] text-warn hover:bg-warn-soft/30"
        >
          <TriangleAlert aria-hidden="true" className="size-3.5 shrink-0" />
          <span className="truncate">
            last refusal {stamp(hunt.lastError.at)} · {tag(hunt.lastError.worker)} ·{' '}
            {hunt.lastError.status} · as {shortAccount(hunt.lastError.account)}
          </span>
          <span className="ml-auto shrink-0 text-accent">open →</span>
        </button>
      ) : null}

      {hunt.live ? null : (
        <p className="border-t border-line/60 px-3 py-1.5 text-[11px] text-faint">
          This is a post-mortem. Nothing here is moving; every figure is as it stood when the
          hunt ended{hunt.endedReason === null ? '' : ` — ${hunt.endedReason}`}. The server keeps
          it for 30 minutes.
        </p>
      )}

      <p className="border-t border-line/60 px-3 py-1.5 text-[10.5px] break-all text-faint">
        GET {hunt.url}
      </p>
    </div>
  )
})

/* ------------------------------------------------------------------ *
 * Search tab — what the shift-list detector sent, and what came back
 * ------------------------------------------------------------------ */

/**
 * Mirrors `GQL_HIT_KEEP` in `schedule-hunt.ts`.
 *
 * Used only to word the heading — "newest 25" rather than a bare count — so a
 * reader knows whether they are looking at every match or the tail of them. If
 * the two drift the label is wrong by a word and nothing else depends on it.
 */
const GQL_HIT_KEEP = 25

/** One call's identity, in one place, for the same reason `requestKey` is. */
function searchCallKey(c: SearchCallDto): string {
  return `${c.at}-${c.reqId}`
}

/**
 * One search, expandable.
 *
 * Collapsed it answers "did this work, and how long did it take"; expanded it
 * answers "why not" — the headers that went out and the body that came back.
 *
 * `queueMs` sits on the COLLAPSED line rather than behind the chevron because it
 * is the only figure here describing the system rather than the request: a
 * concurrency limiter sized below the fleet shows up as tens or hundreds of
 * milliseconds that no per-request timing explains, and nobody would ever find
 * that opening rows one at a time.
 */
const SearchRow = React.memo(function SearchRow({
  c,
  expanded,
  onToggle,
  keyPrefix = '',
}: {
  c: SearchCallDto
  expanded: boolean
  onToggle: (key: string) => void
  /**
   * Namespaces the expand key.
   *
   * The same call appears in both lists on this tab — once in the whole-hunt
   * matches and once in its own worker's ring — and the open row is tracked by a
   * single key. Without a prefix, opening it in one list silently opens it in the
   * other, which reads as a rendering bug.
   */
  keyPrefix?: string
}) {
  const bad = failed(c.status)
  const rowKey = `${keyPrefix}${searchCallKey(c)}`
  const headers = React.useMemo(() => Object.entries(c.headers ?? {}), [c.headers])

  return (
    <div
      className="border-b border-line/25 px-3 py-1 last:border-0"
      style={{
        contentVisibility: 'auto',
        containIntrinsicSize: expanded ? 'auto 220px' : 'auto 24px',
      }}
    >
      <button
        type="button"
        onClick={() => onToggle(rowKey)}
        className="tabular flex w-full items-center gap-2 text-left text-[11px] hover:text-accent"
      >
        <ChevronRight
          className={cn(
            'h-3 w-3 flex-none text-faint transition-transform',
            expanded && 'rotate-90'
          )}
          aria-hidden="true"
        />
        <span className="flex-none text-faint">{stamp(c.at)}</span>
        {/*
          Only in the whole-hunt list. In a worker's own ring every row carries
          the same number, so a column of it would be noise.
        */}
        {keyPrefix === '' ? null : <span className="w-7 flex-none text-faint">s{c.worker}</span>}
        <span className={cn('w-8 flex-none font-medium', bad ? 'text-danger' : 'text-ok')}>
          {c.status === 0 ? 'ERR' : c.status}
        </span>
        {/*
          The match, before any timing — it is the only thing on this row that
          changes what the reader does next. `—` rather than `0 cards` when the
          call never got far enough to count, because a zero here is this
          detector's actual failure mode and must not be manufactured.
        */}
        <span className={cn('w-16 flex-none', c.hit ? 'text-ok' : 'text-faint')}>
          {c.hit ? 'FOUND' : c.cards < 0 ? '—' : `${c.cards} card${c.cards === 1 ? '' : 's'}`}
        </span>
        <span className="w-14 flex-none text-muted">{c.ms}ms</span>
        {/*
          Rendered only when there WAS a wait. A zero on every row trains the eye
          to skip the column, and this is the column that explains a slow fleet.
        */}
        <span className="w-16 flex-none">
          {c.queueMs > 0 ? (
            <span className={cn(c.queueMs > 50 ? 'text-warn' : 'text-faint')}>+{c.queueMs}ms q</span>
          ) : null}
        </span>
        <span className="flex-1 truncate text-right text-faint">{kb(c.bytes)}</span>
      </button>

      {expanded ? (
        <div className="mt-1 ml-5 space-y-1">
          <div className="tabular text-[10.5px] break-all text-faint">
            {c.egress} · ttfb {c.ttfbMs}ms · waf {c.waf ? 'yes' : 'no'}
            {c.reqId === '' ? '' : ` · call ${c.reqId}`}
          </div>

          {headers.length > 0 ? (
            <div>
              <div className="text-[9.5px] font-medium tracking-[0.06em] text-faint uppercase">
                request headers
              </div>
              <div className="tabular mt-0.5 max-h-40 overflow-auto rounded-xs bg-s2/40 p-1.5 text-[10.5px] leading-relaxed">
                {headers.map(([k, v]) => (
                  <div key={k} className="flex gap-1.5 break-all">
                    <span className="flex-none text-faint">{k}:</span>
                    <span className="min-w-0 text-muted/90">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div>
            <div className="text-[9.5px] font-medium tracking-[0.06em] text-faint uppercase">
              response
            </div>
            {c.body ? (
              <pre className="mt-0.5 max-h-52 overflow-auto rounded-xs bg-s2/40 p-1.5 text-[10.5px] leading-relaxed break-words whitespace-pre-wrap text-muted/90">
                {c.body}
              </pre>
            ) : (
              /*
                Said rather than left blank. Only the newest few calls keep a
                body — it is the one field here that is not a number — and an
                empty gap would read as "the server sent nothing back", which is
                a completely different and much more alarming thing.
              */
              <div className="mt-0.5 text-[10.5px] text-faint italic">
                not kept — only the newest few calls carry a body and its headers
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
})

/**
 * Every search one worker made, with the request that produced them.
 *
 * ── Why the payload is at the top and not on every row ──────────────
 * It is byte-identical for every call of a hunt — built once at arm from the job
 * id and the market — so repeating it a dozen times would be a dozen copies of
 * the same string and would bury the rows. It also answers this detector's
 * hardest failure at least as often as any response does: a 200 with no cards is
 * explained by the REQUEST — a wrong job id, a filter that excludes the very
 * schedules being hunted — and all of that is in here.
 *
 * The HEADERS are per-row instead, because they are the half that varies: each
 * worker presents its own fingerprint, and a rotation replaces it. Putting them
 * up here would show one worker's caller as though it were everybody's.
 */
export const SearchTab = React.memo(function SearchTab({
  hunt,
  worker,
  onFocusWorker,
}: {
  hunt: HuntLiveDto
  worker: number | null
  onFocusWorker: (worker: number) => void
}) {
  const [open, setOpen] = React.useState<string | null>(null)
  const [copied, markCopied] = useCopiedFlag()
  const onToggle = React.useCallback((k: string) => setOpen((o) => (o === k ? null : k)), [])
  /*
   * The same tag source every other tab uses.
   *
   * Two of the messages below name a worker that may have no row — a focus that
   * outlived a restart, or a slot that has not reported yet — so they cannot read
   * the role off the row like `roleTag` does. The context carries the fleet
   * boundary, so this cannot disagree with the tags everywhere else.
   */
  const tag = useWorkerTag()

  const gql = hunt.gql ?? null
  const request = gql?.request ?? null
  const onCopy = React.useCallback(() => {
    if (request === null) return
    void navigator.clipboard.writeText(`POST ${request.url}\n\n${request.body}`).then(markCopied, () => {
      /* a denied clipboard is not worth an error state */
    })
  }, [request, markCopied])

  /*
   * Which slots are search workers, so the picker offers only those.
   *
   * Derived from the ROLE rather than from an index range. The layout is watch
   * first then shift list today, and a hard-coded `i >= watchWorkers` here would
   * be a third copy of a fact that only one place should own.
   */
  const searchWorkers = React.useMemo(
    () => hunt.workers.filter((w) => w != null && w.role === 'shift-list'),
    [hunt.workers]
  )

  if (gql === null || gql.workers === 0) {
    /*
     * Two different "off"s, and the wrong advice is worse than none.
     *
     * Nobody turned it on → say how. The hunt REFUSED to arm it → do not tell
     * somebody to add workers they have already added; tell them why the ones
     * they configured could not have worked.
     */
    const refused = hunt.gqlOffReason ?? null
    return (
      <div className="px-3 py-6 text-center">
        <div className={cn('text-[12px]', refused === null ? 'text-muted' : 'text-warn')}>
          {refused === null
            ? 'No shift-list workers are running for this customer.'
            : 'The shift-list fleet was not armed for this customer.'}
        </div>
        <div className="mx-auto mt-1 max-w-md text-[11px] text-faint">
          {refused ??
            'On the Current flow they run only while the assigned shift has no UUID, to read it off the job’s listing, and stand down once it is on file. They never claim.'}
        </div>
      </div>
    )
  }

  const calls = gql.calls ?? []
  /*
   * Whole-hunt and worker-independent, unlike `calls`.
   *
   * A match is what this tab is for, and it is the one record the per-worker ring
   * cannot hold: a dozen slots at a gapless read rate turn over in seconds, so
   * the winning search was always gone before anybody looked. Shown above the
   * per-worker list and without a worker selected, because "which worker caught
   * it" is a column on the row rather than something you must know first.
   */
  const hits = gql.hitCalls ?? []

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* -------- the payload, once -------- */}
      <div className="flex-none border-b border-line/60 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-medium tracking-[0.07em] text-faint uppercase">
            the request every search sends
          </span>
          <span className="tabular text-[10.5px] text-faint">
            {gql.jobId ?? 'no job id'}
          </span>
          <button
            type="button"
            onClick={onCopy}
            className="ml-auto rounded-xs px-1.5 text-[11px] text-accent hover:bg-s2 focus-visible:ring-1 focus-visible:ring-accent focus-visible:outline-none"
          >
            {copied ? 'copied ✓' : 'copy'}
          </button>
        </div>
        <div className="tabular mt-0.5 text-[11px] break-all text-muted">
          POST {request?.url ?? '—'}
        </div>
        {request === null ? null : (
          <pre className="mt-1 max-h-28 overflow-auto rounded-xs bg-s2/40 p-1.5 text-[10.5px] leading-relaxed break-words whitespace-pre-wrap text-muted/90">
            {request.body}
          </pre>
        )}
        <div className="mt-1 text-[10.5px] text-faint">
          Headers vary per worker — each presents its own fingerprint — so they are on the rows
          below rather than here.
        </div>
      </div>

      {/* -------- the matches, whole-hunt -------- */}
      {hits.length > 0 ? (
        <div className="flex-none border-b border-line/60">
          <div className="flex items-center gap-2 px-3 pt-2 pb-1">
            <span className="text-[10px] font-medium tracking-[0.07em] text-faint uppercase">
              matches — the searches that found this schedule
            </span>
            <span className="tabular ml-auto text-[11px] text-faint">
              {hits.length === GQL_HIT_KEEP ? `newest ${GQL_HIT_KEEP}` : hits.length}
            </span>
          </div>
          {/*
            Capped in height rather than in count: every one of these is worth
            reading, and the per-worker list below is what the panel scrolls to.
          */}
          <div className="max-h-44 overflow-auto">
            {[...hits].reverse().map((c) => (
              <SearchRow
                key={`hit-${searchCallKey(c)}`}
                c={c}
                expanded={open === `hit-${searchCallKey(c)}`}
                onToggle={onToggle}
                keyPrefix="hit-"
              />
            ))}
          </div>
        </div>
      ) : null}

      {/* -------- whose calls -------- */}
      <div className="tabular flex flex-none flex-wrap items-center gap-x-2 gap-y-1 border-b border-line/60 px-3 py-1.5 text-[11px]">
        {searchWorkers.length === 0 ? (
          <span className="text-faint">
            {(gql.retired ?? 0) > 0
              ? 'these workers stood down once the shift UUID was found'
              : 'no search workers have reported yet'}
          </span>
        ) : (
          <>
            <span className="text-faint">calls from</span>
            {searchWorkers.map((w) => (
              <button
                key={w.worker}
                type="button"
                onClick={() => onFocusWorker(w.worker)}
                className={cn(
                  'rounded-xs px-1.5',
                  w.worker === worker
                    ? 'bg-accent-soft text-accent'
                    : 'text-muted hover:bg-s2 hover:text-fg'
                )}
              >
                {roleTag(w)}
              </button>
            ))}
            {(gql.retired ?? 0) > 0 ? (
              <span className="text-faint">
                · {gql.retired} more stood down once the shift UUID was found
              </span>
            ) : null}
          </>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {worker === null ? (
          <div className="px-3 py-6 text-center">
            <div className="text-[12px] text-muted">Pick a worker above to see its calls.</div>
            {/*
              The cost is stated because it explains the click. Only the selected
              worker's ring is sent, so an open console costs nothing while
              nothing is selected — which is also why this cannot show them all.
            */}
            <div className="mx-auto mt-1 max-w-md text-[11px] text-faint">
              Only the selected worker&rsquo;s calls are sent, so an open console costs nothing
              while nothing is selected.
            </div>
          </div>
        ) : calls.length === 0 ? (
          <div className="px-3 py-6 text-center">
            <div className="text-[12px] text-muted">No searches recorded for {tag(worker)} yet.</div>
            <div className="mt-1 text-[11px] text-faint">
              {searchWorkers.some((w) => w.worker === worker)
                ? 'Its first search has not landed yet.'
                : `${tag(worker)} is a schedule watcher — it makes no searches. Its calls are on the Log tab.`}
            </div>
          </div>
        ) : (
          /* Newest first, like every other live list in this panel. */
          [...calls]
            .reverse()
            .map((c) => (
              <SearchRow key={searchCallKey(c)} c={c} expanded={open === searchCallKey(c)} onToggle={onToggle} />
            ))
        )}
      </div>
    </div>
  )
})

/* ------------------------------------------------------------------ *
 * GA tab
 * ------------------------------------------------------------------ */

/**
 * One figure of a latency strip, with a target beside it.
 *
 * A bare "queue 310ms" is a number; "310ms against a 2ms target" is a verdict.
 * The colour is the whole point of the component — the strip exists so a
 * fleet's pacing can be read rather than taken on trust.
 */
function LatencyStat({
  label,
  value,
  target,
  unit = 'ms',
}: {
  label: string
  value: number
  target: number | null
  unit?: string
}) {
  const over = target !== null && value > target
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-medium tracking-[0.07em] text-faint uppercase">
        {label}
      </span>
      <span className={cn('tabular text-[13px]', over ? 'text-warn' : 'text-fg')}>
        {value === 0 ? '—' : `${value}${unit}`}
      </span>
    </div>
  )
}

/**
 * The GA fleet's reads: what the job is advertising, and whether we can read it.
 *
 * Deliberately the same shape as `SearchTab` — the request once at the top, the
 * whole-hunt sightings, a worker picker, then the focused worker's rows. Two
 * fleets answer the same kind of question about different endpoints, and a
 * reader who has learned one should not have to learn the other.
 *
 * ── The one number this tab exists for ──────────────────────────────
 * `lastTotal`, and specifically the difference between `0` and null. Zero is the
 * ordinary answer this fleet waits through for hours. Null means no read has
 * produced a number the server could find — a bot check, an error envelope, a
 * truncated body — and before this fleet existed that failure was invisible: a
 * customer whose fleet was being refused looked exactly like a customer whose
 * job was quiet, for as long as they waited, which was forever.
 */
export const GaTab = React.memo(function GaTab({
  hunt,
  worker,
  onFocusWorker,
}: {
  hunt: HuntLiveDto
  worker: number | null
  onFocusWorker: (worker: number) => void
}) {
  const [open, setOpen] = React.useState<string | null>(null)
  const [copied, markCopied] = useCopiedFlag()
  const onToggle = React.useCallback((k: string) => setOpen((o) => (o === k ? null : k)), [])
  const tag = useWorkerTag()

  const ga = hunt.ga ?? null
  const request = ga?.request ?? null
  const onCopy = React.useCallback(() => {
    if (request === null) return
    // The body too, unlike the job detector's — this call is a POST and the body
    // is half of what makes it right or wrong.
    void navigator.clipboard
      .writeText(`POST ${request.url}\n${request.body}`)
      .then(markCopied, () => {
        /* a denied clipboard is not worth an error state */
      })
  }, [request, markCopied])

  // Derived from the ROLE rather than from an index range, for the reason stated
  // on `SearchTab`'s equivalent: the layout is one place's fact to own.
  const gaWorkers = React.useMemo(
    () => hunt.workers.filter((w) => w != null && w.role === 'ga'),
    [hunt.workers]
  )

  if (ga === null || ga.workers === 0) {
    // Two different "off"s — see `SearchTab`. The wrong advice is worse than none.
    const refused = hunt.gaOffReason ?? null
    return (
      <div className="px-3 py-6 text-center">
        <div className={cn('text-[12px]', refused === null ? 'text-muted' : 'text-warn')}>
          {refused === null
            ? 'No GA workers on this run.'
            : 'The GA fleet was not armed for this customer.'}
        </div>
        <div className="mx-auto mt-1 max-w-md text-[11px] text-faint">
          {refused ??
            'They arm only while the assigned shift has no UUID, to read it off the listing, and stand down once it lands. Nothing runs before an application exists.'}
        </div>
      </div>
    )
  }

  const calls = ga.calls ?? []
  const hits = ga.hitCalls ?? []

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* -------- the payload, once -------- */}
      <div className="flex-none border-b border-line/60 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-medium tracking-[0.07em] text-faint uppercase">
            the request every GA read sends
          </span>
          <span className="tabular text-[10.5px] text-faint">{ga.jobId ?? 'no job id'}</span>
          <button
            type="button"
            onClick={onCopy}
            className="ml-auto rounded-xs px-1.5 text-[11px] text-accent hover:bg-s2 focus-visible:ring-1 focus-visible:ring-accent focus-visible:outline-none"
          >
            {copied ? 'copied ✓' : 'copy'}
          </button>
        </div>
        <div className="tabular mt-0.5 text-[11px] break-all text-muted">
          POST {request?.url ?? '—'}
        </div>
        {/*
          The body, shown rather than described.

          A bare job suffix or a wrong `locale` in here answers 200 with an empty
          list forever, which is byte-identical to a job that is simply quiet —
          the exact failure this fleet was built to make visible, so it must not
          be the one thing the tab hides.
        */}
        <div className="tabular mt-0.5 text-[11px] break-all text-faint">
          {request?.body ?? '—'}
        </div>
        <div className="mt-1 text-[10.5px] text-faint">
          Headers vary per worker — each presents its own borrowed identity and fingerprint — so
          they are on the rows below rather than here.
        </div>
      </div>

      {/* -------- what the job is advertising -------- */}
      <div className="flex-none border-b border-line/60 px-3 py-2">
        <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-medium tracking-[0.07em] text-faint uppercase">
              shifts advertised
            </span>
            <span
              className={cn(
                'tabular text-[13px]',
                ga.lastTotal === null ? 'text-warn' : ga.lastTotal > 0 ? 'text-ok' : 'text-fg'
              )}
            >
              {ga.lastTotal === null ? 'unreadable' : ga.lastTotal}
            </span>
          </div>
          <LatencyStat label="queue" value={ga.queueMs} target={2} />
          <LatencyStat label="gap" value={ga.gapMs} target={null} />
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-medium tracking-[0.07em] text-faint uppercase">
              reads
            </span>
            <span className="tabular text-[13px] text-fg">{ga.polls}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-medium tracking-[0.07em] text-faint uppercase">
              errors
            </span>
            <span className={cn('tabular text-[13px]', ga.errors > 0 ? 'text-warn' : 'text-fg')}>
              {ga.errors}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-medium tracking-[0.07em] text-faint uppercase">
              rotations
            </span>
            <span className="tabular text-[13px] text-fg">
              {ga.rotations}
              {ga.stuck > 0 ? <span className="text-warn"> · {ga.stuck} stuck</span> : null}
            </span>
          </div>
        </div>

        {/*
          The failure that used to be invisible, said out loud.

          A fleet reading 200s that carry no total is being refused, and every
          other number on this tab keeps climbing while it happens. Without this
          line it is indistinguishable from a job with nothing open — which is
          also what the customer's drawer says, because there is nothing else to
          say.
        */}
        {ga.lastTotal === null && ga.polls > 0 ? (
          <div className="mt-2 text-[11px] text-warn">
            No read has come back with an <span className="tabular">availableSchedules.total</span>{' '}
            in it. That is what a bot check looks like from here — open a worker below and read the
            body. Nothing will be created until one answers properly.
          </div>
        ) : ga.lastTotal === 0 ? (
          <div className="mt-2 text-[11px] text-muted">
            This job is advertising nothing. The fleet keeps reading; the application is created the
            moment a shift appears.
          </div>
        ) : null}
      </div>

      {/* -------- the sightings, whole-hunt -------- */}
      {hits.length > 0 ? (
        <div className="flex-none border-b border-line/60">
          <div className="flex items-center gap-2 px-3 pt-2 pb-1">
            <span className="text-[10px] font-medium tracking-[0.07em] text-faint uppercase">
              sightings — the reads that found this job advertising
            </span>
            <span className="tabular ml-auto text-[11px] text-faint">
              {hits.length === GQL_HIT_KEEP ? `newest ${GQL_HIT_KEEP}` : hits.length}
            </span>
          </div>
          <div className="max-h-44 overflow-auto">
            {[...hits].reverse().map((c) => (
              <SearchRow
                key={`gahit-${searchCallKey(c)}`}
                c={c}
                expanded={open === `gahit-${searchCallKey(c)}`}
                onToggle={onToggle}
                keyPrefix="gahit-"
              />
            ))}
          </div>
        </div>
      ) : null}

      {/* -------- whose calls -------- */}
      <div className="tabular flex flex-none flex-wrap items-center gap-x-2 gap-y-1 border-b border-line/60 px-3 py-1.5 text-[11px]">
        {gaWorkers.length === 0 ? (
          <span className="text-faint">
            {(ga.retired ?? 0) > 0
              ? 'these workers stood down once the shift UUID was found'
              : 'no GA workers have reported yet'}
          </span>
        ) : (
          <>
            <span className="text-faint">calls from</span>
            {gaWorkers.map((w) => (
              <button
                key={w.worker}
                type="button"
                onClick={() => onFocusWorker(w.worker)}
                className={cn(
                  'rounded-xs px-1.5',
                  w.worker === worker
                    ? 'bg-accent-soft text-accent'
                    : 'text-muted hover:bg-s2 hover:text-fg'
                )}
              >
                {roleTag(w)}
              </button>
            ))}
            {(ga.retired ?? 0) > 0 ? (
              <span className="text-faint">
                · {ga.retired} more stood down once the shift UUID was found
              </span>
            ) : null}
          </>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {worker === null ? (
          <div className="px-3 py-6 text-center">
            <div className="text-[12px] text-muted">Pick a worker above to see its calls.</div>
            <div className="mx-auto mt-1 max-w-md text-[11px] text-faint">
              Only the selected worker&rsquo;s calls are sent, so an open console costs nothing
              while nothing is selected.
            </div>
          </div>
        ) : calls.length === 0 ? (
          <div className="px-3 py-6 text-center">
            <div className="text-[12px] text-muted">No GA reads recorded for {tag(worker)} yet.</div>
            <div className="mt-1 text-[11px] text-faint">
              {gaWorkers.some((w) => w.worker === worker)
                ? 'Its first read has not landed yet.'
                : `${tag(worker)} is not a GA worker — it reads nothing here. Its calls are on the Log or Search tab.`}
            </div>
          </div>
        ) : (
          /* Newest first, like every other live list in this panel. */
          [...calls]
            .reverse()
            .map((c) => (
              <SearchRow
                key={searchCallKey(c)}
                c={c}
                expanded={open === searchCallKey(c)}
                onToggle={onToggle}
              />
            ))
        )}
      </div>
    </div>
  )
})

/* ------------------------------------------------------------------ *
 * Claim tab
 * ------------------------------------------------------------------ */

/**
 * The response that won the shift, in full.
 *
 * ── Why this is separate from the attempt list below it ─────────────
 * The attempts are a log — every try, including the ones that lost, each with a
 * 400-character preview clipped to fit a row. This is the one that carried
 * `data`, and it is not a row: it is the answer, and the reason the run existed.
 * So it gets the whole body rather than a preview, the instants rather than
 * durations derived from them, and the `data` keys listed literally — because
 * "did we get a data key" is the actual stop condition, and a reader should be
 * able to see the keys rather than trust that something checked for them.
 */
const ClaimReceipt = React.memo(function ClaimReceipt({ r }: { r: ClaimReceiptDto }) {
  const tag = useWorkerTag()
  return (
    <div className="border-b border-ok/25 bg-ok-soft/15">
      <div className="tabular flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 pt-2 text-[11.5px]">
        {/*
          Millisecond precision, and stated as an instant rather than as "just
          now". This is the timestamp that gets compared against the schedule,
          against the workers' sightings, and against whatever the account page
          says afterwards — every one of those comparisons needs the absolute
          time, and none of them can use a relative one.
        */}
        <span className="text-[13px] font-medium text-ok">{stamp(r.at)}</span>
        <span className="text-faint">
          sent {stamp(r.sentAt)} · round trip {r.ms}ms
        </span>
        {r.activeToOkMs === null ? null : (
          <span
            className="text-muted"
            title="From a worker reading ACTIVE off the schedule to this response landing — detection, dispatch and the claim's own round trip, end to end."
          >
            ACTIVE→OK {r.activeToOkMs}ms
          </span>
        )}
        <span className="text-faint">
          {tag(r.worker)} · attempt {r.attempt}
        </span>
      </div>

      <div className="tabular flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 pt-1 text-[11px]">
        <span
          className={cn(
            'rounded-xs px-1',
            r.currentState === 'JOB_SELECTED' ? 'bg-ok-soft font-medium text-ok' : 'bg-s3 text-muted'
          )}
        >
          {r.currentState}
        </span>
        {r.previousState ? <span className="text-faint">from {r.previousState}</span> : null}
        {r.adsState ? <span className="text-faint">next {r.adsState}</span> : null}
        {r.selectedAt ? <span className="text-faint">selected {r.selectedAt}</span> : null}
      </div>

      <div className="tabular px-3 pt-1 text-[10.5px] break-all text-faint">
        application {r.applicationId}
        {r.jobId ? ` · job ${r.jobId}` : ''}
        {r.scheduleId ? ` · schedule ${r.scheduleId}` : ''}
      </div>

      {/*
        The keys, listed.

        The stop condition is literally "the response carried a data object", and
        this is that object's contents named one by one. Cheap to render, and it
        turns a claim about the response into something a reader can check.
      */}
      <div className="flex flex-wrap items-center gap-1 px-3 pt-1.5">
        <span className="text-[10px] tracking-[0.06em] text-faint uppercase">
          data · {r.keys.length} keys
        </span>
        {r.keys.map((k) => (
          <span key={k} className="tabular rounded-xs bg-s3 px-1 text-[10.5px] text-muted">
            {k}
          </span>
        ))}
      </div>

      <pre className="mx-3 my-1.5 max-h-64 overflow-auto rounded-xs bg-s2 p-2 text-[10.5px] leading-relaxed break-words whitespace-pre-wrap text-muted/90">
        {r.body}
      </pre>
    </div>
  )
})

/**
 * The call that decides everything.
 *
 * Watching is instrumentation; this is the outcome. `update-application` is the
 * only request in the entire system that changes anything, and whether a shift
 * was won is decided by its response and by nothing else — so this tab shows
 * every attempt verbatim rather than summarised: which try, how long, what
 * status, what state the server reported, and the body it sent back.
 *
 * The body matters more here than anywhere. A lost shift and a broken request
 * look identical from the outside — both are "no shift" — and only what came
 * back distinguishes "somebody else was faster" from "we have been sending a
 * malformed payload all day".
 */
export const ClaimTab = React.memo(function ClaimTab({
  strikes,
  total,
  degraded,
  state,
  claim,
}: {
  /**
   * Every attempt this console holds, oldest first.
   *
   * No longer a window. The server keeps the whole run and ships it a page at a
   * time — a seed on open, then deltas — so this grows for as long as the
   * drawer stays open and is bounded only by what the run did.
   */
  strikes: HuntLiveDto['strikes']
  /** Attempts MADE. Above `strikes.length` when a seed was truncated. */
  total: number | undefined
  /** True when the store could not be read; the rows are then a short tail. */
  degraded: boolean | undefined
  state: string
  /** The `data`-bearing response, when one arrived. Null on every other run. */
  claim: ClaimReceiptDto | null | undefined
}) {
  if (strikes.length === 0 && !claim) {
    return (
      <div className="px-3 py-6 text-center">
        <div className="text-[12px] text-muted">No claim attempted yet</div>
        <div className="mt-1 text-[11px] text-faint">
          The workers are watching for <span className="text-muted">ACTIVE</span>. The instant one
          sees it, the claim fires here — and every attempt is recorded with what came back.
        </div>
      </div>
    )
  }

  const won = state === 'won' || claim != null
  const last = strikes[strikes.length - 1]
  /*
   * Attempts made, and how many of them are on screen.
   *
   * They are equal in the ordinary case and the second line only appears when
   * they are not — a seed truncated at the server's bound, or a store that
   * could not be read and fell back to the process's tail. Showing 300 rows
   * while the run made 8,000 is a fact the reader has to be told; presenting
   * the page count as the total is the bug this whole change removes.
   */
  const made = Math.max(total ?? 0, strikes.length)
  const partial = strikes.length < made
  /*
   * Distinct workers behind the attempts on screen.
   *
   * Counted over exactly the rows below rather than over the run, so the two
   * can never tell different stories — and said as "of the N shown" whenever
   * those are not the same set. Rows from before the field existed are
   * excluded rather than pooled into one phantom worker.
   *
   * Plain, not memoised: the empty-state return above is an early return, so a
   * hook here would be a conditional one. It is one pass over an array the rows
   * below already iterate twice.
   */
  const strikers = new Set(strikes.map((s) => s.worker).filter((w) => w !== undefined)).size

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/*
        The verdict, once, at the top.

        Anyone opening this tab is asking one question — did we get it — and it
        should be answerable without reading a table.
      */}
      <div
        className={cn(
          'flex flex-none flex-wrap items-baseline gap-x-2 border-b px-3 py-2',
          won ? 'border-ok/30 bg-ok-soft/30' : 'border-warn/30 bg-warn-soft/25'
        )}
      >
        <span className={cn('text-[13px] font-medium', won ? 'text-ok' : 'text-warn')}>
          {won ? 'Shift claimed' : 'Not claimed'}
        </span>
        <span className="tabular text-[11.5px] text-faint">
          {made} attempt{made === 1 ? '' : 's'}
          {/*
            How many DETECTORS are behind that count, not just how many calls.

            This is the line that changes what the number means. Twelve attempts
            from twelve workers is a shift the fleet keeps losing to somebody
            faster; twelve from one worker is a single detector re-firing on a
            sighting nobody else corroborated — which is a bug in detection, not
            a lost race. Both read as "12 attempts" without this.
          */}
          {strikers > 1 ? ` from ${strikers} workers` : ''}
          {last ? ` · last ${last.ms}ms` : ''}
          {last?.worker === undefined
            ? ''
            : ` by ${roleTag({ worker: last.worker, role: last.role })}`}
          {last?.state ? ` · ${last.state}` : ''}
        </span>
        {!won && last?.message ? (
          <span className="tabular w-full text-[11px] text-warn/80">{last.message}</span>
        ) : null}
        {/*
          Said only when it needs saying.

          Every attempt of a run is kept, so in the ordinary case the list below
          IS the run and there is nothing to disclose. This line exists for the
          two cases where it is not: a store that could not be read, and a run
          long enough that the first page did not reach the beginning.
        */}
        {degraded ? (
          <span className="tabular w-full text-[11px] text-warn/80">
            The attempt store could not be read — showing the {strikes.length} the process still
            holds.
          </span>
        ) : partial ? (
          <span className="tabular w-full text-[11px] text-faint">
            showing the newest {strikes.length} · the older ones are stored, but a single page does
            not reach them
          </span>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {claim ? <ClaimReceipt r={claim} /> : null}
        {[...strikes].reverse().map((s) => (
          <div
            /*
             * The worker belongs in the key as well as on the row.
             *
             * Every strike in the run pictured was `try 1` — one sighting each,
             * no retry burst — so `at`+`attempt` is only unique because two
             * workers have not yet landed a response in the same millisecond.
             * Several workers strike the same schedule; that is a collision
             * waiting for a busy opening, and React's answer to a duplicate key is to
             * drop a row.
             */
            key={`${s.at}-${s.attempt}-${s.worker ?? ''}`}
            className="border-b border-line/30 px-3 py-1.5 last:border-0"
            style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 56px' }}
          >
            <div className="tabular flex flex-wrap items-center gap-x-2 text-[11.5px]">
              <span className="text-faint">{stamp(s.at)}</span>
              {/*
                Which detector fired this one.

                `roleTag` rather than `useWorkerTag` for the reason given at its
                definition: the row carries the role the server resolved with the
                same `roleOf` that dispatched the strike, so it cannot disagree
                with the fleet counts — and a strike outlives its hunt into the
                post-mortem, where those counts are gone.
              */}
              {s.worker === undefined ? null : (
                <span
                  className="rounded-xs bg-s3 px-1 text-muted"
                  title="The worker whose sighting fired this claim. w = schedule watch, s = shift list, j = job record."
                >
                  {roleTag({ worker: s.worker, role: s.role })}
                </span>
              )}
              <span className="text-fg">try {s.attempt}</span>
              <span className={s.ok ? 'text-ok' : 'text-danger'}>{s.status ?? 'no response'}</span>
              <span className="text-muted">{s.ms}ms</span>
              {s.state ? (
                <span
                  className={cn(
                    'rounded-xs px-1',
                    s.state === 'JOB_SELECTED' ? 'bg-ok-soft text-ok' : 'text-muted'
                  )}
                >
                  {s.state}
                </span>
              ) : null}
              {s.errorCode ? <span className="text-warn">{s.errorCode}</span> : null}
            </div>
            {s.message ? (
              <div className="tabular mt-0.5 text-[11px] text-warn/80">{s.message}</div>
            ) : null}
            {s.body ? (
              <pre className="mt-0.5 max-h-24 overflow-auto text-[10.5px] leading-relaxed whitespace-pre-wrap break-words text-muted/90">
                {s.body}
              </pre>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  )
})
